const INDIAN_STATES_UTS = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Andaman and Nicobar Islands","Chandigarh","Dadra and Nagar Haveli and Daman and Diu","Delhi","Jammu and Kashmir","Ladakh","Lakshadweep","Puducherry"];
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TestRequirements from './TestRequirements';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  updateAdminInventoryQuantity,
  addAdminProductImage,
  changeAdminPassword,
  clearAdminAuditTrail,
  confirmAdminPayment,
  unconfirmAdminPayment,
  deleteAdminOrder,
  deleteAdminFarmer,
  deleteAdminProduct,
  deleteAdminProductImage,
  deleteAdminProductImageByUrl,
  setAdminPrimaryProductImage,
  deleteUploadedProductImage,
  createAdminFarmer,
  createAdminProduct,
  copyAdminProduct,
  getAdminAuditTrail,
  getAdminCustomerFunnel,
  getAdminWebTraffic,
  getAdminFarmers,
  getAdminInventoryProfile,
  getAdminOrders,
  createAdminOrder,
  editAdminOrder,
  askAdminOperationsAssistant,
  getAdminProducts,
  getAdminSummary,
  lookupPincode,
  setAdminProductActive,
  updateAdminFarmer,
  updateAdminOrderStatus,
  updateAdminOrderTracker,
  updateAdminProduct,
  uploadAdminProductImage,
  uploadAdminFarmerImage,
  spellcheckAdminFarmer,
  replaceAdminProductImage,
} from '@/lib/api';
import { getAdminProductDrafts, saveAdminProductDraft, deleteAdminProductDraft } from '@/lib/api';
import { toast } from 'sonner';
import {
  Activity,
  Boxes,
  ClipboardList,
  History,
  Home,
  IndianRupee,
  LayoutDashboard,
  MessageSquareText,
  PackagePlus,
  Plus,
  Sprout,
  Trash2,
  FlaskConical,
} from 'lucide-react';

// India Post contractual Other States tariff. Reserve 500 g for outer packing
// so a 1 kg product uses the 1001–1500 g slab, per operations policy.
const INDIA_POST_OTHER_STATES = [
  [500, 35], [1000, 57], [1500, 80], [2000, 115],
  [3000, 145], [4000, 175], [5000, 205],
];
const suggestedShipping = (size, unit) => {
  const amount = Number(size);
  if (!Number.isFinite(amount) || amount <= 0) return '';
  const gramsPerUnit = { mg: 0.001, g: 1, kg: 1000, ml: 1, L: 1000 };
  if (!(unit in gramsPerUnit)) return ''; // Count packs require manual shipping.
  const chargeableGrams = amount * gramsPerUnit[unit] + 500;
  const base = INDIA_POST_OTHER_STATES.find(([max]) => chargeableGrams <= max)?.[1]
    ?? 205 + Math.ceil((chargeableGrams - 5000) / 1000) * 30;
  return String(Math.ceil(base * 1.18));
};

const PRICING_FIELDS = ['farmer_price', 'packaging', 'shipping', 'testing', 'platform_fee', 'platform_fee_discount'];

const evaluatePricingExpression = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return 0;

  const expression = raw.startsWith('=') ? raw.slice(1).trim() : raw;
  if (!/^[0-9+\-*/().\s]+$/.test(expression)) return null;

  try {
    const result = Function(`"use strict"; return (${expression})`)();
    return Number.isFinite(result) ? Math.round(result * 100) / 100 : null;
  } catch {
    return null;
  }
};

const pricingValue = (variant, field) => {
  const formula = variant.formulas?.[field];
  if (formula !== undefined && formula !== '') {
    const result = evaluatePricingExpression(formula);
    return result === null ? 0 : (field === 'shipping' ? Math.ceil(result) : result);
  }
  const value = Number(variant[field] || 0);
  return field === 'shipping' ? Math.ceil(value) : value;
};

const SHIPPING_DEADLINE_HOURS = 72;
const SHIPPING_WARNING_HOURS = 48;

const shippingTiming = (order, nowMs = Date.now()) => {
  // Shipping promise: 24–72 hours from order placement. The row status is
  // measured against the latest promised ship time (72 hours after placement).
  if (!order?.created_at) return { state: 'unknown', ageHours: 0, delayHours: 0 };
  const orderPlacedMs = new Date(order.created_at).getTime();
  if (!Number.isFinite(orderPlacedMs)) return { state: 'unknown', ageHours: 0, delayHours: 0 };

  const deadlineMs = orderPlacedMs + SHIPPING_DEADLINE_HOURS * 60 * 60 * 1000;

  if (order.shipped_at) {
    const shippedMs = new Date(order.shipped_at).getTime();
    return {
      state: shippedMs <= deadlineMs ? 'on-time' : 'late',
      ageHours: (shippedMs - orderPlacedMs) / (60 * 60 * 1000),
      delayHours: Math.max(0, (shippedMs - deadlineMs) / (60 * 60 * 1000)),
    };
  }

  if (order.shipped_date) {
    // The tracker historically stored only a calendar date. Treat the end of
    // that India-local day as the shipment time so "on time" is conservative.
    const shippedEndOfDayMs = new Date(`${order.shipped_date}T23:59:59+05:30`).getTime();
    return {
      state: shippedEndOfDayMs <= deadlineMs ? 'on-time' : 'late',
      ageHours: (shippedEndOfDayMs - orderPlacedMs) / (60 * 60 * 1000),
      delayHours: Math.max(0, (shippedEndOfDayMs - deadlineMs) / (60 * 60 * 1000)),
    };
  }

  const ageHours = (nowMs - orderPlacedMs) / (60 * 60 * 1000);
  if (ageHours >= SHIPPING_DEADLINE_HOURS) return { state: 'overdue', ageHours, delayHours: ageHours - SHIPPING_DEADLINE_HOURS };
  if (ageHours >= SHIPPING_WARNING_HOURS) return { state: 'warning', ageHours, delayHours: 0 };
  return { state: 'open', ageHours, delayHours: 0 };
};

const DailyOrdersChart = ({ orders }) => {
  const days = Array.from({ length: 14 }, (_, index) => {
    const date = new Date();
    date.setDate(date.getDate() - (13 - index));
    const key = date.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    return {
      key,
      label: date.toLocaleDateString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: 'numeric',
        month: 'short',
      }),
      count: 0,
    };
  });

  const byDay = Object.fromEntries(days.map((day) => [day.key, day]));
  orders.forEach((order) => {
    if (!order.created_at) return;
    const key = new Date(order.created_at).toLocaleDateString('en-CA', {
      timeZone: 'Asia/Kolkata',
    });
    if (byDay[key]) byDay[key].count += 1;
  });

  const totalOrdersToDate = orders.length;
  const maxCount = Math.max(1, ...days.map((day) => day.count));
  const width = 700;
  const height = 180;
  const padX = 24;
  const padY = 22;
  const points = days
    .map((day, index) => {
      const x = padX + (index * (width - padX * 2)) / (days.length - 1);
      const y = height - padY - (day.count / maxCount) * (height - padY * 2);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-end justify-between gap-3 mb-3">
          <div>
            <h2 className="text-lg font-semibold">{totalOrdersToDate} orders received to date</h2>
            <p className="text-sm text-muted-foreground">Last 14 days · India time</p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[620px]">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-44" role="img" aria-label="Daily orders curve">
              <line x1={padX} y1={height - padY} x2={width - padX} y2={height - padY} stroke="currentColor" opacity="0.15" />
              <polyline points={points} fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
              {days.map((day, index) => {
                const x = padX + (index * (width - padX * 2)) / (days.length - 1);
                const y = height - padY - (day.count / maxCount) * (height - padY * 2);
                return (
                  <g key={day.key}>
                    <circle cx={x} cy={y} r="4" fill="currentColor" />
                    <text x={x} y={Math.max(13, y - 10)} textAnchor="middle" fontSize="11" fill="currentColor">
                      {day.count}
                    </text>
                  </g>
                );
              })}
            </svg>
            <div className="grid grid-cols-7 gap-1 text-[11px] text-muted-foreground">
              {days.map((day) => (
                <div key={day.key} className="text-center">{day.label}</div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const tabs = [
  { key: 'Dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'Farmers', label: 'Farmers', icon: Sprout },
  { key: 'Products', label: 'Products', icon: PackagePlus },
  { key: 'Inventory', label: 'Inventory Profile', icon: Boxes },
  { key: 'Testing', label: 'Test Requirements', icon: FlaskConical },
  { key: 'Orders', label: 'Order Tracker', icon: ClipboardList },
  { key: 'Funnel', label: 'Web traffic', icon: Activity },
  { key: 'Audit', label: 'Audit Trail', icon: History },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('Dashboard');
  const adminUsername = sessionStorage.getItem('0middle_admin_username') || 'Admin';
  const [farmers, setFarmers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderEditor, setOrderEditor] = useState(null);
  const [savingOrderEditor, setSavingOrderEditor] = useState(false);
  const [orderFilters, setOrderFilters] = useState({ name: '', phone: '', address: '' });
  const filteredOrders = orders.filter((order) => {
    const includes = (value, query) => String(value ?? '').toLowerCase().includes(query.trim().toLowerCase());
    const address = order.delivery_address || {};
    return includes(order.customer?.name, orderFilters.name) &&
      includes(order.customer?.phone, orderFilters.phone) &&
      includes([address.line1, address.line2, address.city, address.state, address.pincode].filter(Boolean).join(' '), orderFilters.address);
  });
  const [summary, setSummary] = useState(null);
  const [auditTrail, setAuditTrail] = useState([]);
  const [inventoryProfile, setInventoryProfile] = useState([]);
  const [customerFunnel, setCustomerFunnel] = useState(null);
  const [webTraffic, setWebTraffic] = useState([]);
  const [assistantQuery, setAssistantQuery] = useState('');
  const [assistantResponse, setAssistantResponse] = useState(null);
  const [assistantAction, setAssistantAction] = useState(null);
  const [assistantActionBusy, setAssistantActionBusy] = useState(false);
  const [analyticalResponse, setAnalyticalResponse] = useState(null);
  const [assistantConversation, setAssistantConversation] = useState([]);
  const [assistantBusy, setAssistantBusy] = useState(false);
  const assistantScrollRef = useRef(null);
  useEffect(() => {
    if (assistantScrollRef.current) assistantScrollRef.current.scrollTop = assistantScrollRef.current.scrollHeight;
  }, [assistantConversation, assistantBusy]);
  const assistantRequestId = useRef(0);
  const resetOperationsAssistant = () => {
    assistantRequestId.current += 1;
    setAssistantQuery('');
    setAssistantResponse(null);
    setAnalyticalResponse(null);
    setAssistantConversation([]);
    setAssistantBusy(false);
  };
  const [loading, setLoading] = useState(true);
  const [imageUploading, setImageUploading] = useState(false);
  const [replacingImageFor, setReplacingImageFor] = useState('');
  const [cardPricingDrafts, setCardPricingDrafts] = useState({});
  const [savingCardPricing, setSavingCardPricing] = useState('');
  const [copyingProductSlug, setCopyingProductSlug] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [farmerForm, setFarmerForm] = useState({
    name: '',
    pincode: '',
    village: '',
    city: '',
    district: '',
    state: '',
    address: '',
    fssai: '',
    phone: '',
    headline: '',
    story: '',
    photo_url: '',
    photo_urls: [],
  });
  const [spellcheckDialog, setSpellcheckDialog] = useState(null);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [editingFarmerSlug, setEditingFarmerSlug] = useState('');
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [orderPincodeLoading, setOrderPincodeLoading] = useState(false);

  const [productForm, setProductForm] = useState({
    general_availability: true,
    available_states: [],
    farmer_slug: '',
    name: '',
    description: '',
    pack_size_value: '',
    pack_size_unit: 'ml',
    farmer_price: '',
    platform_fee: '50',
    platform_fee_discount: '0',
    shipping: '0',
    packaging: '0',
    testing: '0',
    variants: [
      {
        variant_id: '',
        pack_size_value: '',
        pack_size_unit: 'ml',
        farmer_price: '',
        packaging: '',
        shipping: '',
        testing: '',
        platform_fee: '50',
        platform_fee_discount: '0',
        comments: {},
        formulas: {},
      },
    ],
    image_url: '',
    image_object_key: '',
    images: [],
    initial_inventory_quantity: '',
    initial_inventory_unit: 'L',
  });
  const [productDrafts, setProductDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const refreshProductDrafts = async () => {
    try {
      const { data } = await getAdminProductDrafts();
      setProductDrafts(data || []);
    } catch {
      toast.error('Unable to load saved product drafts');
    }
  };
  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const { data } = await saveAdminProductDraft(productForm, currentDraftId);
      setCurrentDraftId(data.draft_id);
      await refreshProductDrafts();
      toast.success('Draft saved permanently. You can resume it later.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to save draft');
    } finally {
      setSavingDraft(false);
    }
  };
  useEffect(() => {
    refreshProductDrafts();
  }, []);

  const [editingProductSlug, setEditingProductSlug] = useState('');
  const [selectedProductImageIndex, setSelectedProductImageIndex] = useState(null);

  const [inventoryDrafts, setInventoryDrafts] = useState({});
  const [savingInventorySlug, setSavingInventorySlug] = useState('');
  const [postingInventorySlug, setPostingInventorySlug] = useState('');

  const openOrderEditor = (order = null) => {
    const address = order?.delivery_address || {};
    setOrderEditor({
      id: order?.order_id || null,
      customer_name: order?.customer?.name || '',
      customer_phone: order?.customer?.phone || '',
      line1: address.line1 || '', line2: address.line2 || '',
      city: address.city || '', district: address.district || '', state: address.state || '', pincode: address.pincode || '',
      product_slug: order?.item?.product_slug || products[0]?.slug || '',
      variant_id: order?.item?.variant_id || '',
      quantity: order?.item?.quantity || 1,
      order_total: order?.pricing?.total ?? 0,
      reason: '',
      purchase_declared_date: order?.customer_declared_paid_at ? new Date(order.customer_declared_paid_at).toISOString().slice(0, 16) : '',
    });
  };

  const handleOrderPincode = async (value) => {
    const pincode = value.replace(/\D/g, '').slice(0, 6);
    setOrderEditor(previous => ({ ...previous, pincode, ...(pincode.length === 6 ? {} : { city: '', district: '', state: '' }) }));
    if (pincode.length !== 6) return;
    setOrderPincodeLoading(true);
    try {
      const { data } = await lookupPincode(pincode);
      setOrderEditor(previous => previous?.pincode === pincode ? ({ ...previous, city: data.city || '', district: data.district || '', state: data.state || '' }) : previous);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to find PIN code');
    } finally {
      setOrderPincodeLoading(false);
    }
  };

  const saveOrderEditor = async () => {
    if (!orderEditor || savingOrderEditor) return;
    setSavingOrderEditor(true);
    try {
      const payload = {
        customer_name: orderEditor.customer_name.trim(),
        customer_phone: orderEditor.customer_phone.trim(),
        delivery_address: {
          line1: orderEditor.line1, line2: orderEditor.line2 || null,
          city: orderEditor.city, district: orderEditor.district, state: orderEditor.state, pincode: orderEditor.pincode,
        },
        product_slug: orderEditor.product_slug,
        variant_id: orderEditor.variant_id || null,
        quantity: Number(orderEditor.quantity),
        order_total: Number(orderEditor.order_total),
        reason: orderEditor.reason,
        purchase_declared_date: orderEditor.purchase_declared_date || null,
      };
      if (orderEditor.id) {
        await editAdminOrder(orderEditor.id, payload);
      } else {
        await createAdminOrder(payload);
      }
      toast.success(orderEditor.id ? 'Order updated' : 'Order created; payment not confirmed');
      setOrderEditor(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to save order');
    } finally {
      setSavingOrderEditor(false);
    }
  };

  const load = async () => {
    try {
      const [farmersRes, productsRes, ordersRes, summaryRes] =
        await Promise.all([
          getAdminFarmers(),
          getAdminProducts(),
          getAdminOrders(),
          getAdminSummary(),
        ]);

      setFarmers(farmersRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setSummary(summaryRes.data);

      const optionalResults = await Promise.allSettled([
        getAdminAuditTrail(),
        getAdminInventoryProfile(),
        getAdminCustomerFunnel(),
        getAdminWebTraffic(),
      ]);

      if (optionalResults[0].status === 'fulfilled') {
        setAuditTrail(optionalResults[0].value.data);
      }
      if (optionalResults[1].status === 'fulfilled') {
        setInventoryProfile(optionalResults[1].value.data);
      }
      if (optionalResults[2].status === 'fulfilled') {
        setCustomerFunnel(optionalResults[2].value.data);
      }
      if (optionalResults[3].status === 'fulfilled') {
        setWebTraffic(optionalResults[3].value.data);
      }

      optionalResults.forEach((result, index) => {
        if (result.status === 'rejected') {
          const labels = ['Audit Trail', 'Inventory Profile', 'Customer Funnel', 'Web traffic'];
          console.warn(`${labels[index]} failed to load`, result.reason);
        }
      });
    } catch (error) {
      console.error(error);

      if (error?.response?.status === 401) {
        sessionStorage.removeItem('0middle_admin_token');
        sessionStorage.removeItem('0middle_admin_username');
        navigate('/admin/login');
        return;
      }

      toast.error(
        error?.response?.data?.detail ||
          'Unable to load core admin data'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionStorage.getItem('0middle_admin_token')) {
      navigate('/admin/login');
      return;
    }

    load();
    const refresh = setInterval(load, 10000);
    return () => clearInterval(refresh);
  }, []);

  const copyProduct = async (product) => {
    setCopyingProductSlug(product.slug);
    try {
      const response = await copyAdminProduct(product.slug);
      await load();
      toast.success(`New batch created: ${response.data.batch_number}. Find it in Inventory Profile.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to copy product');
    } finally {
      setCopyingProductSlug('');
    }
  };

  const logout = () => {
    sessionStorage.removeItem('0middle_admin_token');
    sessionStorage.removeItem('0middle_admin_username');
    navigate('/admin/login');
  };

  const passwordChecks = {
    length: passwordForm.new_password.length >= 12,
    lower: /[a-z]/.test(passwordForm.new_password),
    upper: /[A-Z]/.test(passwordForm.new_password),
    number: /[0-9]/.test(passwordForm.new_password),
    special: /[^A-Za-z0-9]/.test(passwordForm.new_password),
  };
  const strongPassword = Object.values(passwordChecks).every(Boolean);

  const savePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (!strongPassword) {
      toast.error('New password does not meet all security requirements');
      return;
    }

    setPasswordChanging(true);

    try {
      await changeAdminPassword(
        passwordForm.current_password,
        passwordForm.new_password
      );

      toast.success('Admin password changed. Please sign in again.');
      sessionStorage.removeItem('0middle_admin_token');
      sessionStorage.removeItem('0middle_admin_username');
      navigate('/admin/login');
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Unable to change admin password'
      );
    } finally {
      setPasswordChanging(false);
    }
  };

  const resetFarmerForm = () => {
    setFarmerForm({
      name: '',
      pincode: '',
      village: '',
      city: '',
      district: '',
      state: '',
      address: '',
      fssai: '',
      phone: '',
      headline: '',
      story: '',
      photo_url: '',
      photo_urls: [],
    });
    setEditingFarmerSlug('');
  };

  const handleFarmerPincode = async (value) => {
    const pincode = value.replace(/\D/g, '').slice(0, 6);
    setFarmerForm((previous) => ({ ...previous, pincode }));

    if (pincode.length !== 6) return;

    setPincodeLoading(true);
    try {
      const response = await lookupPincode(pincode);
      setFarmerForm((previous) => ({
        ...previous,
        pincode,
        village: response.data.village || response.data.post_offices?.[0] || '',
        city: response.data.city || '',
        district: response.data.district || '',
        state: response.data.state || '',
      }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to find PIN code');
    } finally {
      setPincodeLoading(false);
    }
  };

  const countWords = (value) =>
    String(value || '').trim() ? String(value).trim().split(/\s+/).length : 0;

  const handleFarmerPhotoUpload = async (file) => {
    if (!file) return;
    const current = farmerForm.photo_urls?.length ? farmerForm.photo_urls : (farmerForm.photo_url ? [farmerForm.photo_url] : []);
    if (current.length >= 2) {
      toast.error('A farmer can have a maximum of 2 photos');
      return;
    }
    try {
      const response = await uploadAdminFarmerImage(file, farmerForm.name || 'farmer');
      setFarmerForm((previous) => {
        const existing = previous.photo_urls?.length ? previous.photo_urls : (previous.photo_url ? [previous.photo_url] : []);
        const photo_urls = [...existing, response.data.image_url].filter(Boolean).slice(0, 2);
        return { ...previous, photo_urls, photo_url: photo_urls[0] || '' };
      });
      toast.success('Farmer photo uploaded');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to upload farmer photo');
    }
  };

  const removeFarmerPhoto = (index) => {
    setFarmerForm((previous) => {
      const existing = previous.photo_urls?.length ? previous.photo_urls : (previous.photo_url ? [previous.photo_url] : []);
      const photo_urls = existing.filter((_, photoIndex) => photoIndex !== index);
      return { ...previous, photo_urls, photo_url: photo_urls[0] || '' };
    });
  };

  const applySpellingSuggestions = (value, issues) => {
    let updated = value || '';
    issues.forEach((issue) => {
      if (!issue.suggestion) return;
      updated = updated.replace(new RegExp('\\b' + issue.word + '\\b', 'gi'), issue.suggestion);
    });
    return updated;
  };

  const persistFarmer = async (form) => {
    const photos = (form.photo_urls?.length ? form.photo_urls : (form.photo_url ? [form.photo_url] : [])).filter(Boolean).slice(0, 2);
    const payload = {
      ...form,
      phone: form.phone || null,
      fssai: form.fssai || null,
      headline: form.headline || null,
      story: form.story || null,
      photo_url: photos[0] || null,
      photo_urls: photos,
      address: form.address || null,
      village: form.village || null,
    };

    if (editingFarmerSlug) {
      const response = await updateAdminFarmer(editingFarmerSlug, payload);
      toast.success('Farmer updated');
      setSelectedFarmer(response.data);
    } else {
      const response = await createAdminFarmer(payload);
      toast.success('Farmer added');
      setSelectedFarmer(response.data);
    }
    resetFarmerForm();
    await load();
  };

  const saveFarmer = async () => {
    try {
      if (countWords(farmerForm.headline) > 10) { toast.error('Farmer headline must be 10 words or fewer'); return; }
      if (countWords(farmerForm.story) > 120) { toast.error('Farmer description must be 120 words or fewer'); return; }
      const spelling = await spellcheckAdminFarmer(farmerForm.headline, farmerForm.story);
      if (spelling.data.issues?.length) {
        setSpellcheckDialog({ issues: spelling.data.issues, form: { ...farmerForm } });
        return;
      }
      await persistFarmer(farmerForm);
    } catch (error) {
      toast.error(error?.response?.data?.detail || (editingFarmerSlug ? 'Unable to update farmer' : 'Unable to add farmer'));
    }
  };

  const keepFarmerSpelling = async () => {
    const form = spellcheckDialog?.form;
    setSpellcheckDialog(null);
    if (!form) return;
    try { await persistFarmer(form); } catch (error) { toast.error(error?.response?.data?.detail || 'Unable to save farmer'); }
  };

  const acceptFarmerSpelling = async () => {
    if (!spellcheckDialog) return;
    const corrected = {
      ...spellcheckDialog.form,
      headline: applySpellingSuggestions(spellcheckDialog.form.headline, spellcheckDialog.issues),
      story: applySpellingSuggestions(spellcheckDialog.form.story, spellcheckDialog.issues),
    };
    setFarmerForm(corrected);
    setSpellcheckDialog(null);
    try {
      await persistFarmer(corrected);
      toast.success('Suggested spelling changes applied');
    } catch (error) { toast.error(error?.response?.data?.detail || 'Unable to save farmer'); }
  };

  const editFarmer = (farmer) => {
    setEditingFarmerSlug(farmer.slug);
    setFarmerForm({
      name: farmer.name || '',
      pincode: farmer.pincode || '',
      village: farmer.village || '',
      city: farmer.city || '',
      district: farmer.district || '',
      state: farmer.state || '',
      address: farmer.address || '',
      fssai: farmer.fssai || '',
      phone: farmer.phone || '',
      headline: farmer.headline || '',
      story: farmer.story || '',
      photo_url: farmer.photo_url || farmer.photo_urls?.[0] || '',
      photo_urls: farmer.photo_urls?.length ? farmer.photo_urls.slice(0, 2) : (farmer.photo_url ? [farmer.photo_url] : []),
    });
    setSelectedFarmer(farmer);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const removeFarmer = async (farmer) => {
    const confirmed = window.confirm(
      `Delete ${farmer.name}? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await deleteAdminFarmer(farmer.slug);
      toast.success('Farmer deleted');
      if (selectedFarmer?.slug === farmer.slug) setSelectedFarmer(null);
      if (editingFarmerSlug === farmer.slug) resetFarmerForm();
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to delete farmer');
    }
  };

  const exportFarmersCsv = () => {
    const headers = [
      'Farmer Name',
      'Pincode',
      'City',
      'District',
      'State',
      'Address',
      'FSSAI',
      'Phone Number',
      'Story',
    ];

    const escapeCsv = (value) => {
      const text = value == null ? '' : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const rows = farmers.map((farmer) => [
      farmer.name || '',
      farmer.pincode || '',
      farmer.city || farmer.village || '',
      farmer.district || '',
      farmer.state || '',
      farmer.address || '',
      farmer.fssai || '',
      farmer.phone || '',
      farmer.story || '',
    ]);

    const csv = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row) => row.map(escapeCsv).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `0middle-farmers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const resetProductForm = () => {
    setProductForm({
      general_availability: true,
      available_states: [],
      farmer_slug: '',
      name: '',
      description: '',
      pack_size_value: '',
      pack_size_unit: 'ml',
      farmer_price: '',
      platform_fee: '50',
      shipping: '',
      packaging: '',
      testing: '',
      variants: [
        {
          variant_id: '',
          pack_size_value: '',
          pack_size_unit: 'ml',
          farmer_price: '',
          packaging: '',
          shipping: '',
          testing: '',
          platform_fee: '50',
          platform_fee_discount: '0',
          comments: {},
          formulas: {},
        },
      ],
      image_url: '',
      image_object_key: '',
      images: [],
      initial_inventory_quantity: '',
      initial_inventory_unit: 'L',
    });
    setEditingProductSlug('');
    setCurrentDraftId(null);
    setSelectedProductImageIndex(null);
  };

  const selectProductForEdit = (slug) => {
    if (!slug) {
      resetProductForm();
      return;
    }

    const product = products.find((item) => item.slug === slug);
    if (!product) return;

    const match = String(product.pack_size || '').match(/^\s*(\d+(?:\.\d+)?)\s*([A-Za-z]+)\s*$/);

    const productVariants = (product.variants?.length
      ? product.variants
      : [{
          variant_id: 'default',
          pack_size: product.pack_size,
          farmer_price: product.farmer_price,
          platform_fee: product.platform_fee,
          shipping: product.shipping,
          packaging: product.packaging ?? product.handling,
          testing: product.testing,
        }]
    ).map((variant) => {
      const variantMatch = String(variant.pack_size || '').match(/^\s*(\d+(?:\.\d+)?)\s*([A-Za-z]+)\s*$/);
      return {
        variant_id: variant.variant_id || '',
        pack_size_value: String(variant.pack_size_value ?? variantMatch?.[1] ?? ''),
        pack_size_unit: variant.pack_size_unit || variantMatch?.[2] || 'ml',
        farmer_price: String(variant.farmer_price ?? ''),
        packaging: String(variant.packaging ?? variant.handling ?? 0),
        shipping: String(variant.shipping ?? 0),
        testing: String(variant.testing ?? 0),
        platform_fee: String(variant.original_platform_fee ?? variant.platform_fee ?? 0),
      platform_fee_discount: String(variant.platform_fee_discount ?? 0),
        comments: variant.comments || {},
        formulas: variant.formulas || {},
      };
    });

    const primaryVariant = productVariants[0];

    setEditingProductSlug(slug);
    setSelectedProductImageIndex(null);
    setProductForm({
      general_availability: product.general_availability !== false,
      available_states: product.available_states || [],
      farmer_slug: product.farmer_slug || '',
      name: product.name || '',
      description: product.description || '',
      pack_size_value: primaryVariant.pack_size_value,
      pack_size_unit: primaryVariant.pack_size_unit,
      farmer_price: primaryVariant.farmer_price,
      platform_fee: primaryVariant.platform_fee,
      platform_fee_discount: primaryVariant.platform_fee_discount,
      shipping: primaryVariant.shipping,
      packaging: primaryVariant.packaging,
      testing: primaryVariant.testing,
      variants: productVariants,
      image_url: product.image_url || '',
      image_object_key: '',
      images: (product.image_urls?.length ? product.image_urls : (product.image_url ? [product.image_url] : []))
        .map((url) => ({ url, object_key: '' })),
      initial_inventory_quantity: '',
      initial_inventory_unit: product.inventory_unit || 'L',
    });
  };

  const updateProductVariant = (index, field, value) => {
    setProductForm((previous) => {
      const variants = previous.variants.map((variant, variantIndex) => {
        if (variantIndex !== index) return variant;
        const next = { ...variant, [field]: value };
        if ((field === 'pack_size_value' || field === 'pack_size_unit') && !variant.shippingManuallyEdited) {
          next.shipping = suggestedShipping(
            field === 'pack_size_value' ? value : variant.pack_size_value,
            field === 'pack_size_unit' ? value : variant.pack_size_unit
          );
          next.formulas = { ...(variant.formulas || {}), shipping: '' };
        }
        if (field === 'shipping') next.shippingManuallyEdited = true;
        return next;
      });
      if (!productForm.general_availability && !productForm.available_states?.length) { toast.error('Select at least one state or UT'); return; }
      const primary = variants[0];
      return {
        ...previous,
        variants,
        ...(index === 0
          ? {
              pack_size_value: primary.pack_size_value,
              pack_size_unit: primary.pack_size_unit,
              farmer_price: primary.farmer_price,
              packaging: primary.packaging,
              shipping: primary.shipping,
              testing: primary.testing,
              platform_fee: primary.platform_fee,
            }
          : {}),
      };
    });
  };

  const updatePricingExpression = (index, field, rawValue) => {
    setProductForm((previous) => {
      const variants = previous.variants.map((variant, variantIndex) => {
        if (variantIndex !== index) return variant;

        const result = evaluatePricingExpression(rawValue);
        return {
          ...variant,
          ...(result !== null ? { [field]: String(result) } : {}),
          ...(field === 'shipping' ? { shippingManuallyEdited: true } : {}),
          formulas: {
            ...(variant.formulas || {}),
            [field]: rawValue,
          },
        };
      });

      const primary = variants[0];
      return {
        ...previous,
        variants,
        ...(index === 0
          ? {
              farmer_price: primary.farmer_price,
              packaging: primary.packaging,
              shipping: primary.shipping,
              testing: primary.testing,
              platform_fee: primary.platform_fee,
            }
          : {}),
      };
    });
  };

  const editPricingComment = (index, field) => {
    const current = productForm.variants[index]?.comments?.[field] || '';
    const next = window.prompt('Comment for this pricing box', current);
    if (next === null) return;

    setProductForm((previous) => ({
      ...previous,
      variants: previous.variants.map((variant, variantIndex) =>
        variantIndex === index
          ? {
              ...variant,
              comments: {
                ...(variant.comments || {}),
                [field]: next.trim(),
              },
            }
          : variant
      ),
    }));
  };

  const renderPricingCell = (variant, index, field) => {
    const comment = variant.comments?.[field] || '';
    const formula = variant.formulas?.[field];
    const displayValue =
      formula !== undefined && formula !== ''
        ? formula
        : String(variant[field] ?? '');

    return (
      <div
        className="relative w-28"
        title={comment || undefined}
      >
        <Input
          type="text"
          inputMode="decimal"
          value={field === 'shipping' && !variant.formulas?.[field] && displayValue !== '' && Number.isFinite(Number(displayValue)) ? String(Math.ceil(Number(displayValue))) : displayValue}
          onChange={(event) =>
            updatePricingExpression(index, field, event.target.value)
          }
          onFocus={(event) => {
            if (String(displayValue).trim() === '0') {
              event.target.select();
            }
          }}
          onBlur={() => {
            if (displayValue && evaluatePricingExpression(displayValue) === null) {
              toast.error('Use numbers and + - * / ( ) only');
            } else if (field === 'shipping' && displayValue) {
              updatePricingExpression(index, field, String(Math.ceil(evaluatePricingExpression(displayValue) || 0)));
            }
          }}
          className={`w-28 pr-8 ${
            comment ? 'border-primary/60' : ''
          }`}
          placeholder="0 or =10+5"
        />
        <button
          type="button"
          onClick={() => editPricingComment(index, field)}
          className={`absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center ${
            comment
              ? 'text-primary bg-primary/10'
              : 'text-muted-foreground hover:bg-muted'
          }`}
          title={comment || 'Add comment'}
          aria-label={comment ? 'Edit pricing comment' : 'Add pricing comment'}
        >
          <MessageSquareText className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const addProductVariant = () => {
    setProductForm((previous) => ({
      ...previous,
      variants: [
        ...previous.variants,
        {
          variant_id: '',
          pack_size_value: '',
          pack_size_unit: previous.variants[0]?.pack_size_unit || 'g',
          farmer_price: '',
          packaging: '',
          shipping: '',
          shippingManuallyEdited: false,
          testing: '',
          platform_fee: previous.variants[0]?.platform_fee || '50',
          comments: {},
          formulas: {},
        },
      ],
    }));
  };

  const removeProductVariant = (index) => {
    if (productForm.variants.length === 1) {
      toast.error('A product needs at least one pack size');
      return;
    }
    setProductForm((previous) => {
      const variants = previous.variants.filter((_, variantIndex) => variantIndex !== index);
      const primary = variants[0];
      return {
        ...previous,
        variants,
        pack_size_value: primary.pack_size_value,
        pack_size_unit: primary.pack_size_unit,
        farmer_price: primary.farmer_price,
        packaging: primary.packaging,
        shipping: primary.shipping,
        testing: primary.testing,
        platform_fee: primary.platform_fee,
      };
    });
  };

  const groupedWebTraffic = useMemo(() => {
    const groups = new Map();

    webTraffic.forEach((entry) => {
      // Group by IP when available. Older events may not have IP captured yet,
      // so fall back to visitor ID to avoid merging unrelated anonymous traffic.
      const groupKey = entry.ip_address || `visitor:${entry.visitor_id || 'unknown'}`;
      const existing = groups.get(groupKey) || {
        key: groupKey,
        ip_address: entry.ip_address || null,
        visitors: new Set(),
        events: [],
        city: entry.city,
        region: entry.region,
        country: entry.country,
        timezone: entry.timezone,
        language: entry.language,
        screen: entry.screen,
        referrer: entry.referrer,
      };

      if (entry.visitor_id) existing.visitors.add(entry.visitor_id);
      existing.events.push(entry);

      // Keep the best available metadata for the grouped row.
      existing.city = existing.city || entry.city;
      existing.region = existing.region || entry.region;
      existing.country = existing.country || entry.country;
      existing.timezone = existing.timezone || entry.timezone;
      existing.language = existing.language || entry.language;
      existing.screen = existing.screen || entry.screen;
      existing.referrer = existing.referrer || entry.referrer;

      groups.set(groupKey, existing);
    });

    return Array.from(groups.values())
      .map((group) => {
        const events = [...group.events].sort(
          (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)
        );
        return {
          ...group,
          visitors: Array.from(group.visitors),
          events,
          first_seen: events[0]?.created_at || null,
          last_seen: events[events.length - 1]?.created_at || null,
          converted: events.some((event) => event.event === 'PURCHASE_DECLARED'),
        };
      })
      .sort(
        (a, b) => new Date(b.last_seen || 0) - new Date(a.last_seen || 0)
      );
  }, [webTraffic]);

  const trafficTrend = useMemo(() => {
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const currentStart = now - 7 * dayMs;
    const previousStart = now - 14 * dayMs;

    const visitorSets = {
      current: new Set(),
      previous: new Set(),
    };

    webTraffic.forEach((entry) => {
      const timestamp = new Date(entry.created_at || 0).getTime();
      if (!Number.isFinite(timestamp)) return;
      const visitorKey = entry.visitor_id || entry.ip_address;
      if (!visitorKey) return;

      if (timestamp >= currentStart && timestamp <= now) {
        visitorSets.current.add(visitorKey);
      } else if (timestamp >= previousStart && timestamp < currentStart) {
        visitorSets.previous.add(visitorKey);
      }
    });

    const current = visitorSets.current.size;
    const previous = visitorSets.previous.size;
    const delta = current - previous;
    const percent = previous > 0 ? Math.round((delta / previous) * 100) : null;

    let direction = 'flat';
    if (delta > 0) direction = 'up';
    if (delta < 0) direction = 'down';

    return { current, previous, delta, percent, direction };
  }, [webTraffic]);

  const uploadProductImages = async (files) => {
    const selectedFiles = Array.from(files || []);
    if (!selectedFiles.length) return;

    setImageUploading(true);

    try {
      if (editingProductSlug) {
        let latestImages = productForm.images.map((image) => image.url);

        for (const file of selectedFiles) {
          const response = await addAdminProductImage(editingProductSlug, file);
          latestImages = response.data.image_urls || [
            ...latestImages,
            response.data.image_url,
          ].filter(Boolean);
        }

        setProductForm((previous) => ({
          ...previous,
          image_url: latestImages[0] || '',
          image_object_key: '',
          images: latestImages.map((url) => ({ url, object_key: '' })),
        }));
        await load();
      } else {
        const uploadedImages = [];

        for (const file of selectedFiles) {
          const response = await uploadAdminProductImage(file, productForm.name);
          uploadedImages.push({
            url: response.data.image_url,
            object_key: response.data.object_key,
          });
        }

        setProductForm((previous) => {
          const images = [...previous.images, ...uploadedImages];
          return {
            ...previous,
            image_url: images[0]?.url || '',
            image_object_key: images[0]?.object_key || '',
            images,
          };
        });
      }

      toast.success(
        selectedFiles.length === 1
          ? 'Product image uploaded'
          : `${selectedFiles.length} product images uploaded`
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Unable to upload image'
      );
    } finally {
      setImageUploading(false);
    }
  };

  const setPrimaryProductImage = async (index) => {
    const image = productForm.images[index];
    if (!image || index === 0) return;

    const reordered = [
      image,
      ...productForm.images.filter((_, imageIndex) => imageIndex !== index),
    ];

    if (!editingProductSlug) {
      setProductForm((previous) => ({
        ...previous,
        image_url: reordered[0]?.url || '',
        image_object_key: reordered[0]?.object_key || '',
        images: reordered,
      }));
      setSelectedProductImageIndex(0);
      return;
    }

    try {
      const response = await setAdminPrimaryProductImage(
        editingProductSlug,
        image.url
      );
      const urls = response.data.image_urls || reordered.map((item) => item.url);

      setProductForm((previous) => ({
        ...previous,
        image_url: response.data.image_url || urls[0] || '',
        image_object_key: '',
        images: urls.map((url) => ({ url, object_key: '' })),
      }));

      setSelectedProductImageIndex(0);
      toast.success('Primary product image updated');
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Unable to change primary product image'
      );
    }
  };

  const removeProductFormImage = async (index) => {
    const image = productForm.images[index];
    if (!image) return;

    try {
      if (editingProductSlug) {
        await deleteAdminProductImageByUrl(editingProductSlug, image.url);
      } else if (image.object_key) {
        await deleteUploadedProductImage(image.object_key);
      }

      setProductForm((previous) => {
        const images = previous.images.filter((_, imageIndex) => imageIndex !== index);
        return {
          ...previous,
          image_url: images[0]?.url || '',
          image_object_key: images[0]?.object_key || '',
          images,
        };
      });
      setSelectedProductImageIndex((current) => {
        if (current === null) return null;
        if (current === index) return null;
        return current > index ? current - 1 : current;
      });
      toast.success('Product image removed');
      if (editingProductSlug) await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to remove image');
    }
  };

  const removeExistingProductImage = async (productSlug) => {
    try {
      await deleteAdminProductImage(productSlug);
      toast.success('Product image removed');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to remove image');
    }
  };

  const removeProduct = async (product) => {
    const confirmed = window.confirm(
      `Delete ${product.name}? This cannot be undone. Products with order history cannot be deleted.`
    );
    if (!confirmed) return;

    try {
      await deleteAdminProduct(product.slug);
      toast.success('Product deleted');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to delete product');
    }
  };

  const replaceProductImage = async (productSlug, file) => {
    if (!file) return;

    setReplacingImageFor(productSlug);

    try {
      await replaceAdminProductImage(productSlug, file);
      toast.success('Product image updated');
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Unable to replace image'
      );
    } finally {
      setReplacingImageFor('');
    }
  };

  const saveProduct = async () => {
    try {
      const invalidFormula = productForm.variants.some((variant) =>
        PRICING_FIELDS.some((field) => {
          const formula = variant.formulas?.[field];
          return formula && evaluatePricingExpression(formula) === null;
        })
      );

      if (invalidFormula) {
        toast.error('Fix the invalid pricing calculation before saving');
        return;
      }

      const variants = productForm.variants.map((variant) => ({
        variant_id: variant.variant_id || null,
        pack_size_value: Number(variant.pack_size_value),
        pack_size_unit: variant.pack_size_unit,
        farmer_price: pricingValue(variant, 'farmer_price'),
        packaging: pricingValue(variant, 'packaging'),
        shipping: Math.ceil(pricingValue(variant, 'shipping')),
        testing: pricingValue(variant, 'testing'),
        platform_fee: pricingValue(variant, 'platform_fee'),
        platform_fee_discount: pricingValue(variant, 'platform_fee_discount'),
        comments: variant.comments || {},
        formulas: variant.formulas || {},
      }));

      if (variants.some((variant) => variant.platform_fee_discount > variant.platform_fee || variant.platform_fee_discount < 0)) {
      toast.error('Platform discount must be between zero and the original platform fee');
      return;
    }
    if (variants.some((variant) => !variant.pack_size_value)) {
        toast.error('Enter a pack size for every denomination');
        return;
      }

      const primary = variants[0];
      const payload = {
        farmer_slug: productForm.farmer_slug,
        name: productForm.name,
        description: productForm.description || null,
        general_availability: productForm.general_availability !== false,
        available_states: productForm.general_availability ? [] : (productForm.available_states || []),
        pack_size_value: primary.pack_size_value,
        pack_size_unit: primary.pack_size_unit,
        farmer_price: primary.farmer_price,
        platform_fee: primary.platform_fee,
        platform_fee_discount: primary.platform_fee_discount,
        shipping: primary.shipping,
        packaging: primary.packaging,
        testing: primary.testing,
        variants,
        image_urls: productForm.images.map((image) => image.url),
      };

      if (editingProductSlug) {
        await updateAdminProduct(editingProductSlug, payload);
        toast.success(
          products.some((product) => product.slug === editingProductSlug && product.active)
            ? 'Product updated and remains posted on 0middle.com'
            : 'Product updated'
        );
      } else {
        await createAdminProduct({
          ...payload,
          image_url: productForm.images[0]?.url || null,
          initial_inventory_quantity: Number(
            productForm.initial_inventory_quantity || 0
          ),
          initial_inventory_unit: ({ mg: 'g', g: 'g', kg: 'kg', ml: 'ml', L: 'L', count: 'count' })[primary.pack_size_unit] || 'g',
        });
        toast.success('Product added as unposted. Post it from Inventory Profile when ready.');
      }

      if (currentDraftId) {
        try {
          await deleteAdminProductDraft(currentDraftId);
          await refreshProductDrafts();
        } catch {
          toast.error('Product saved, but its old draft could not be removed');
        }
      }
      resetProductForm();
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          (editingProductSlug ? 'Unable to update product' : 'Unable to add product')
      );
    }
  };

  const saveInventoryQuantity = async (row) => {
    if (row.active) {
      toast.error("UNPOST the batch before editing original available quantity");
      return;
    }
    if (Number(row.original_available_quantity ?? 0) > 0 && Number(row.remaining_quantity ?? 0) <= 0) {
      toast.error("Sold-out batches cannot be edited");
      return;
    }
    const value = inventoryDrafts[row.product_slug];
    const quantity = Number(value);
    if (value === '' || value == null || !Number.isFinite(quantity) || quantity < 0) {
      toast.error('Enter a valid non-negative quantity');
      return;
    }
    if (quantity < Number(row.ordered_quantity || 0)) {
      toast.error('Original quantity cannot be less than ordered quantity');
      return;
    }
    setSavingInventorySlug(row.product_slug);
    try {
      await updateAdminInventoryQuantity(row.product_slug, quantity);
      toast.success('Original inventory quantity updated');
      setInventoryDrafts((previous) => {
        const next = { ...previous };
        delete next[row.product_slug];
        return next;
      });
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to update inventory quantity');
    } finally {
      setSavingInventorySlug('');
    }
  };

  const setInventoryPosted = async (row) => {
    if (!row.active && Number(row.remaining_quantity ?? 0) <= 0) {
      toast.error('Enter and save available inventory before posting');
      return;
    }
    setPostingInventorySlug(row.product_slug);
    try {
      await setAdminProductActive(row.product_slug, !row.active);
      toast.success(row.active ? 'Batch unposted' : 'Batch posted to 0middle.com');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to change posting status');
    } finally {
      setPostingInventorySlug('');
    }
  };

  const getCardPricingVariants = (product) =>
    (product.variants?.length
      ? product.variants
      : [{
          variant_id: 'default',
          pack_size: product.pack_size,
          farmer_price: product.farmer_price,
          packaging: product.packaging ?? product.handling,
          shipping: product.shipping,
          testing: product.testing,
          platform_fee: product.original_platform_fee ?? product.platform_fee,
          platform_fee_discount: product.platform_fee_discount ?? 0,
          comments: {},
          formulas: {},
        }]
    ).map((variant) => ({
      ...variant,
      farmer_price: String(variant.farmer_price ?? 0),
      packaging: String(variant.packaging ?? variant.handling ?? 0),
      shipping: String(variant.shipping ?? 0),
      testing: String(variant.testing ?? 0),
      platform_fee: String(variant.original_platform_fee ?? variant.platform_fee ?? 0),
      platform_fee_discount: String(variant.platform_fee_discount ?? 0),
    }));

  const cardPricingState = (product) =>
    cardPricingDrafts[product.slug] || {
      variants: getCardPricingVariants(product),
      dirty: false,
    };

  const updateCardPricing = (product, variantIndex, field, value) => {
    setCardPricingDrafts((previous) => {
      const current = previous[product.slug] || {
        variants: getCardPricingVariants(product),
        dirty: false,
      };
      return {
        ...previous,
        [product.slug]: {
          dirty: true,
          variants: current.variants.map((variant, index) =>
            index === variantIndex ? { ...variant, [field]: value } : variant
          ),
        },
      };
    });
  };

  const saveCardPricing = async (product) => {
    const draft = cardPricingState(product);
    if (!draft.dirty) return;

    const variants = draft.variants.map((variant) => ({
      variant_id: variant.variant_id || null,
      pack_size_value: Number(
        variant.pack_size_value ??
        String(variant.pack_size || '').match(/^\s*(\d+(?:\.\d+)?)\s*/)?.[1]
      ),
      pack_size_unit:
        variant.pack_size_unit ||
        String(variant.pack_size || '').match(/^\s*\d+(?:\.\d+)?\s*([A-Za-z]+)\s*$/)?.[1] ||
        'g',
      farmer_price: Number(variant.farmer_price || 0),
      packaging: Number(variant.packaging || 0),
      shipping: Math.ceil(Number(variant.shipping || 0)),
      testing: Number(variant.testing || 0),
      platform_fee: Number(variant.platform_fee || 0),
      platform_fee_discount: Number(variant.platform_fee_discount || 0),
      platform_fee_discount: Number(variant.platform_fee_discount || 0),
      comments: variant.comments || {},
      formulas: {},
    }));

    if (variants.some((variant) => !variant.pack_size_value)) {
      toast.error('A pack size is missing');
      return;
    }

    setSavingCardPricing(product.slug);
    try {
      const primary = variants[0];
      await updateAdminProduct(product.slug, {
        farmer_slug: product.farmer_slug,
        name: product.name,
        description: product.description || null,
        pack_size_value: primary.pack_size_value,
        pack_size_unit: primary.pack_size_unit,
        farmer_price: primary.farmer_price,
        packaging: primary.packaging,
        shipping: primary.shipping,
        testing: primary.testing,
        platform_fee: primary.platform_fee,
        platform_fee_discount: primary.platform_fee_discount,
        variants,
        image_urls: product.image_urls || (product.image_url ? [product.image_url] : []),
      });

      // Pricing edits are staged for explicit reposting. If the product was
      // live, unpost it after saving so the admin deliberately posts the new price.
      if (product.active) {
        await setAdminProductActive(product.slug, false);
      }

      setCardPricingDrafts((previous) => {
        const next = { ...previous };
        delete next[product.slug];
        return next;
      });
      toast.success(
        product.active
          ? 'Pricing saved. Product is unposted — review and Post when ready.'
          : 'Pricing saved. Post when ready.'
      );
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to save pricing');
    } finally {
      setSavingCardPricing('');
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await updateAdminOrderStatus(orderId, status);
      toast.success(`Order marked ${status.toLowerCase()}`);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to update order');
    }
  };

  const updateOrderTracker = async (orderId, payload, successMessage = 'Order updated') => {
    try {
      await updateAdminOrderTracker(orderId, payload);
      toast.success(successMessage);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to update order');
    }
  };

  const clearAuditTrail = async () => {
    if (!window.confirm('Delete every audit trail entry? This cannot be undone.')) {
      return;
    }

    try {
      const response = await clearAdminAuditTrail();
      setAuditTrail([]);
      toast.success(`Deleted ${response.data?.deleted_count || 0} audit entries`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Unable to clear audit trail');
    }
  };

  const exportOrdersCsv = () => {
    const headers = [
      'Customer Name',
      'Phone Number',
      'Batch',
      'Product',
      'Farmer',
      'Amount (INR)',
      'Quantity',
      'Purchase Declared',
      'Payment Confirmed',
      'Farmer Paid',
      'Farmer Paid By',
      'Farmer Paid At',
      'Order Received',
      'Farmer Communicated',
      'Farmer Communicated By',
      'Farmer Communicated At',
      'Order Shipped',
      'Tracking Label',
      'Shipping Provider',
      'Customer Communicated',
      'Customer Communicated By',
      'Customer Communicated At',
      'Shipping Weight (kg)',
      'Shipping Cost (INR)',
      'Customer Received',
      'Customer Remarks',
    ];

    const escapeCsv = (value) => {
      const text = value == null ? '' : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const rows = orders.map((order) => [
      order.customer?.name || '',
      order.customer?.phone || '',
      order.batch || '',
      order.item?.name || '',
      order.farmer?.name || '',
      order.pricing?.total ?? '',
      order.item?.quantity ?? '',
      order.customer_declared_paid_at || '',
      order.payment_confirmed_at || '',
      order.farmer_paid ? 'Yes' : 'No',
      order.farmer_paid_updated_by || order.farmer_paid_by || '',
      order.farmer_paid_updated_at || order.farmer_paid_at || '',
      order.created_at || '',
      order.farmer_communicated ? 'Yes' : 'No',
      order.farmer_communicated_by || '',
      order.farmer_communicated_at || '',
      order.shipped_date || order.shipped_at || '',
      order.tracking_label || '',
      order.shipping_provider || '',
      order.customer_communicated ? 'Yes' : 'No',
      order.customer_communicated_by || '',
      order.customer_communicated_at || '',
      order.shipping_weight_kg ?? '',
      order.shipping_cost_inr ?? '',
      order.customer_received_date || order.delivered_at || '',
      order.customer_remarks || '',
    ]);

    const csv = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row) => row.map(escapeCsv).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `0middle-order-tracker-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportProductsCsv = () => {
    const headers = [
      'Product Name',
      'Farmer',
      'Description',
      'Pack Size',
      'Farmer Price (INR)',
      'Platform Fee (INR)',
      'Shipping (INR)',
      'Packaging (INR)',
      'Testing (INR)',
      'Total (INR)',
      'Batch Number',
      'Status',
      'Image URL',
      'Initial / Total Inventory Added',
      'Inventory Unit',
      'Available Quantity',
      'Reserved Quantity',
      'Sold Quantity',
      'Created At',
      'Updated At',
    ];

    const escapeCsv = (value) => {
      const text = value == null ? '' : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const farmerName = (slug) =>
      farmers.find((farmer) => farmer.slug === slug)?.name || slug || '';

    const rows = products.map((product) => {
      const packaging = Number(product.packaging ?? product.handling ?? 0);
      const testing = Number(product.testing || 0);
      const total =
        Number(product.farmer_price || 0) +
        Number(product.platform_fee || 0) +
        Number(product.shipping || 0) +
        packaging +
        testing;

      return [
        product.name || '',
        farmerName(product.farmer_slug),
        product.description || '',
        product.pack_size || '',
        product.farmer_price ?? '',
        product.platform_fee ?? '',
        product.shipping ?? '',
        packaging,
        testing,
        total,
        product.batch_number || '',
        product.active ? 'Posted' : 'Unposted',
        product.image_url || '',
        product.inventory?.total_added_display ?? product.inventory_total_added ?? '',
        product.inventory?.display_unit ?? product.inventory_unit ?? '',
        product.inventory?.available_display ?? product.inventory_available ?? '',
        product.inventory?.reserved_display ?? product.inventory_reserved ?? '',
        product.inventory?.sold_display ?? product.inventory_sold ?? '',
        product.created_at || '',
        product.updated_at || '',
      ];
    });

    const csv = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row) => row.map(escapeCsv).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `0middle-products-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportInventoryCsv = () => {
    const headers = [
      'Product Name',
      'Farmer Name',
      'Batch Number',
      'Original Available Quantity',
      'Ordered Quantity',
      'Remaining Quantity',
    ];

    const escapeCsv = (value) => {
      const text = value == null ? '' : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };

    const rows = inventoryProfile.map((row) => [
      row.product_name || '',
      row.farmer_name || '',
      row.batch_number || '',
      `${row.original_available_quantity ?? 0} ${row.unit || ''}`.trim(),
      `${row.ordered_quantity ?? 0} ${row.unit || ''}`.trim(),
      `${row.remaining_quantity ?? 0} ${row.unit || ''}`.trim(),
    ]);

    const csv = [
      headers.map(escapeCsv).join(','),
      ...rows.map((row) => row.map(escapeCsv).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `0middle-inventory-profile-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const confirmPayment = async (orderId) => {
    try {
      await confirmAdminPayment(orderId);
      toast.success('Payment confirmed');
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Unable to confirm payment'
      );
    }
  };

  const unconfirmPayment = async (orderId) => {
    try {
      await unconfirmAdminPayment(orderId);
      toast.success('Payment confirmation removed');
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Unable to remove payment confirmation'
      );
    }
  };

  const deleteOrder = async (order) => {
    const customerName = order.customer?.name || 'this customer';
    const confirmed = order.payment_status === 'PAYMENT_CONFIRMED';
    const message = confirmed
      ? `Delete order ${order.order_id} for ${customerName}? This will permanently remove the order and reverse its sold inventory and payment ledger entry.`
      : `Delete order ${order.order_id} for ${customerName}? This will permanently remove the order and release any reserved inventory.`;

    if (!window.confirm(message)) return;

    try {
      await deleteAdminOrder(order.order_id);
      toast.success('Order deleted');
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Unable to delete order'
      );
    }
  };

  const pendingOrders = useMemo(
    () => orders.filter((order) => order.payment_status === 'PAYMENT_SUBMITTED'),
    [orders]
  );

  const openDashboardDestination = (destination) => {
    setTab(destination);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const runOperationsAssistant = async (queryOverride = null) => {
    const rawQuery =
      typeof queryOverride === 'string'
        ? queryOverride
        : assistantQuery;
    const query = rawQuery.trim().toLowerCase() || 'things to do today';
    const requestId = ++assistantRequestId.current;
    if (!['things to do today', 'ordering highlights', 'customer behavior'].includes(query)) {
      // Resolve customer-name follow-ups locally from authenticated Order Tracker data.
      // This avoids sending customer identities to the AI and prevents generic privacy refusals.
      if (/\b(names?|who)\b/i.test(rawQuery) && /\b(customer|buyer|names?|who|purchaser)\b/i.test(rawQuery)) {
        const context = [rawQuery, ...assistantConversation.slice(-4).map((turn) => turn.question || '')].join(' ').toLowerCase();
        const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
        const candidates = products.filter((product) => {
          const name = normalize(product.name);
          const words = name.split(' ').filter((word) => word.length >= 4 && !['farmer', 'pure', 'natural', 'powder', 'cold', 'pressed'].includes(word));
          return name && (context.includes(name) || words.some((word) => context.includes(word)));
        });
        const product = candidates.sort((a, b) => normalize(b.name).length - normalize(a.name).length)[0];
        if (product) {
          const customers = new Map();
          const matchedOrders = orders.filter((order) => {
            const item = order.item || {};
            return item.product_slug === product.slug || normalize(item.name).includes(normalize(product.name)) || normalize(product.name).includes(normalize(item.name)) && Boolean(item.name);
          }).filter((order) => order.payment_status === 'PAYMENT_CONFIRMED');
          matchedOrders.forEach((order) => {
            const name = String(order.customer?.name || '').trim();
            if (!name) return;
            const key = name.toLowerCase();
            const row = customers.get(key) || { name, order_ids: [] };
            row.order_ids.push(order.order_id);
            customers.set(key, row);
          });
          const rows = Array.from(customers.values());
          const result = { answer: `${rows.length} distinct customer(s) with confirmed payment for ${product.name} (${matchedOrders.length} confirmed orders). Customer names are listed below.`, customers: rows, orders: [], steps: [], method: 'Authenticated Order Tracker lookup' };
          setAssistantResponse(null);
          setAnalyticalResponse(result);
          setAssistantConversation((prev) => [...prev, { kind: 'analytics', question: rawQuery.trim(), answer: result }]);
          setAssistantQuery('');
          return;
        }
      }
      setAssistantResponse(null);
      setAssistantBusy(true);
      try {
        const result = await askAdminOperationsAssistant(rawQuery.trim(), assistantConversation.filter((turn) => turn.kind === 'analytics').slice(-6).map(({ question, answer }) => ({ question, answer: answer.answer })));
        if (requestId === assistantRequestId.current) {
          setAnalyticalResponse(result.data);
          setAssistantConversation((prev) => [...prev, { kind: 'analytics', question: rawQuery.trim(), answer: result.data }]);
          setAssistantQuery('');
        }
      } catch (error) {
        if (requestId === assistantRequestId.current) {
          const failure = { answer: error.response?.data?.detail || 'Could not calculate an answer. Please retry.', steps: [], orders: [] };
          setAnalyticalResponse(failure);
          setAssistantConversation((prev) => [...prev, { kind: 'analytics', question: rawQuery.trim(), answer: failure }]);
        }
      } finally {
        if (requestId === assistantRequestId.current) setAssistantBusy(false);
      }
      return;
    }
    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    const paymentsToVerify = orders.filter(
      (order) => order.payment_status === 'PAYMENT_SUBMITTED'
    );

    const paidUnshipped = orders.filter((order) => {
      if (order.payment_status !== 'PAYMENT_CONFIRMED') return false;
      if (['SHIPPED', 'DELIVERED'].includes(order.status)) return false;
      if (!order.created_at) return false;
      return now - new Date(order.created_at).getTime() >= oneDay;
    });

    const readyToFulfill = orders.filter(
      (order) =>
        order.payment_status === 'PAYMENT_CONFIRMED' &&
        !['SHIPPED', 'DELIVERED'].includes(order.status)
    );

    const lowInventory = products.filter(
      (product) =>
        product.active &&
        Number(product.sellable_pack_count || 0) <= 2
    );

    const last24Hours = orders.filter(
      (order) =>
        order.created_at &&
        now - new Date(order.created_at).getTime() <= oneDay
    );

    const orderRevenue24h = last24Hours.reduce(
      (sum, order) => sum + Number(order.pricing?.total || 0),
      0
    );

    const productTotals = {};
    orders.forEach((order) => {
      const name = order.item?.name || 'Unknown product';
      productTotals[name] =
        (productTotals[name] || 0) + Number(order.item?.quantity || 0);
    });
    const topProduct = Object.entries(productTotals).sort(
      (a, b) => b[1] - a[1]
    )[0];

    const checkoutStarted = Number(customerFunnel?.checkout_started || 0);
    const purchased = Number(customerFunnel?.purchased || 0);
    const conversion =
      checkoutStarted > 0
        ? Math.round((purchased / checkoutStarted) * 100)
        : 0;

    const actions = [];

    if (paymentsToVerify.length) {
      actions.push({
        title: `Verify ${paymentsToVerify.length} payment${paymentsToVerify.length === 1 ? '' : 's'}`,
        detail: paymentsToVerify.map((order) => `${order.order_id} · ${order.customer?.name || 'Unknown customer'} · ₹${Number(order.pricing?.total || 0).toLocaleString('en-IN')}`).join(' | '),
        destination: 'Orders',
        kind: 'payment', orderIds: paymentsToVerify.map((order) => order.order_id),
      });
    }

    paidUnshipped.forEach((order) => {
      const ageHours = Math.floor(
        (now - new Date(order.created_at).getTime()) / (60 * 60 * 1000)
      );
      actions.push({
        title: `Did order ${order.order_id} ship?`,
        detail: `It has been about ${ageHours} hours since this paid order came through.`,
        destination: 'Orders', kind: 'shipping', orderIds: [order.order_id],
      });
    });

    if (readyToFulfill.length && paidUnshipped.length === 0) {
      actions.push({
        title: `${readyToFulfill.length} paid order${readyToFulfill.length === 1 ? '' : 's'} ready for fulfillment`,
        detail: 'Payment is confirmed and shipment has not yet been recorded.',
        destination: 'Orders', kind: 'shipping', orderIds: readyToFulfill.map((order) => order.order_id),
      });
    }

    // Customer follow-ups are based on the India-local shipment calendar date.
    const todayIndia = new Date(now + 330 * 60 * 1000).toISOString().slice(0, 10);
    const shipmentDay = (order) => order.shipped_date
      || (order.shipped_at ? new Date(new Date(order.shipped_at).getTime() + 330 * 60 * 1000).toISOString().slice(0, 10) : '');
    orders.forEach((order) => {
      const shippedDay = shipmentDay(order);
      if (!shippedDay) return;
      const customerName = order.customer?.name || 'Customer';
      if (!order.customer_communicated && shippedDay <= todayIndia) {
        actions.push({
          title: `Has customer ${customerName} been informed of shipment?`,
          detail: `Order ${order.order_id} shipped on ${shippedDay}. Record customer communication.`,
          destination: 'Orders', kind: 'customer_communication', orderIds: [order.order_id],
        });
      }
      const elapsedDays = Math.floor((Date.parse(todayIndia + 'T00:00:00Z') - Date.parse(shippedDay + 'T00:00:00Z')) / 86400000);
      if (elapsedDays === 1 && !order.customer_received_date && !order.delivered_at) {
        actions.push({
          title: `Is order ${order.order_id}, shipped yesterday, on track?`,
          detail: `${customerName} · Shipped ${shippedDay}. Check courier status and customer notification.`,
          destination: 'Orders', kind: 'delivery_followup', orderIds: [order.order_id],
        });
      }
      if (elapsedDays >= 3 && !order.customer_received_date && !order.delivered_at) {
        actions.push({
          title: `Has order ${order.order_id} been delivered, or is it on track?`,
          detail: `Shipped ${elapsedDays} days ago (${shippedDay}) · ${customerName}. Confirm delivery or record tracking follow-up.`,
          destination: 'Orders', kind: 'delivery_followup', orderIds: [order.order_id],
        });
      }
    });

    if (lowInventory.length) {
      actions.push({
        title: `Review low inventory for ${lowInventory.length} product${lowInventory.length === 1 ? '' : 's'}`,
        detail: lowInventory
          .map((product) => `${product.name}: ${product.sellable_pack_count || 0} packs`)
          .join(' · '),
        destination: 'Inventory', kind: 'inventory',
      });
    }

    if (Number(customerFunnel?.abandoned_count || 0) > 0) {
      actions.push({
        title: `${customerFunnel.abandoned_count} abandoned checkout${customerFunnel.abandoned_count === 1 ? '' : 's'} to review`,
        detail: 'These visitors started checkout but did not declare a purchase within 30 minutes.',
        destination: 'Funnel', kind: 'funnel',
      });
    }

    const highlights = [
      {
        label: 'Orders in last 24h',
        value: last24Hours.length,
        detail: `₹${orderRevenue24h.toLocaleString('en-IN')} order value`,
      },
      {
        label: 'Most ordered product',
        value: topProduct ? topProduct[0] : '—',
        detail: topProduct ? `${topProduct[1]} unit${topProduct[1] === 1 ? '' : 's'} ordered` : 'No order history yet',
      },
      {
        label: 'Checkout conversion',
        value: `${conversion}%`,
        detail: `${purchased} purchase declaration${purchased === 1 ? '' : 's'} from ${checkoutStarted} checkout${checkoutStarted === 1 ? '' : 's'}`,
      },
    ];

    const behavior = [
      `${customerFunnel?.visitors || 0} unique browser sessions in the last ${customerFunnel?.window_days || 30} days.`,
      `${customerFunnel?.payment_viewed || 0} reached the payment screen.`,
      `${customerFunnel?.abandoned_count || 0} abandoned checkout before declaring payment.`,
    ];

    const wantsActions =
      query.includes('today') ||
      query.includes('action') ||
      query.includes('todo') ||
      query.includes('to do') ||
      (!query.includes('customer') && !query.includes('highlight'));

    const wantsHighlights =
      query.includes('highlight') ||
      query.includes('order') ||
      query.includes('today');

    const wantsBehavior =
      query.includes('customer') ||
      query.includes('behaviour') ||
      query.includes('behavior') ||
      query.includes('today');

    setAssistantResponse({
      query: rawQuery.trim() || 'things to do today',
      actions: wantsActions ? actions : [],
      highlights: wantsHighlights ? highlights : [],
      behavior: wantsBehavior ? behavior : [],
    });
  };

  if (loading) {
    return <div className="p-8">Loading admin...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/0middle-logo.svg"
              alt="0middle logo"
              className="w-9 h-9 object-contain"
            />
            <div>
              <p
                className="text-xl font-semibold tracking-tight"
                style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              >
                <span
                  className="font-bold"
                  style={{ fontFamily: "'SF Mono', Menlo, Consolas, monospace", fontVariantNumeric: 'slashed-zero', fontFeatureSettings: "'zero' 1" }}
                >
                  0
                </span>
                middle Admin
              </p>
              <p className="text-xs text-muted-foreground">Internal operations</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={() => navigate('/')}
              className="gap-2"
            >
              <Home className="w-4 h-4" />
              Home
            </Button>

            <div className="text-sm font-medium px-2">
              {adminUsername}
            </div>

            <Button
              onClick={() => setShowPasswordChange(true)}
            >
              Change password
            </Button>

            <Button onClick={logout}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      {showPasswordChange && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6 space-y-4">
              <div>
                <h2 className="text-xl font-semibold">Change admin password</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your current password, then choose a new password.
                </p>
              </div>

              <Field
                label="Current password"
                type="password"
                value={passwordForm.current_password}
                onChange={(value) =>
                  setPasswordForm({
                    ...passwordForm,
                    current_password: value,
                  })
                }
              />

              <Field
                label="New password"
                type="password"
                value={passwordForm.new_password}
                onChange={(value) =>
                  setPasswordForm({
                    ...passwordForm,
                    new_password: value,
                  })
                }
              />

              <div className="rounded-lg border bg-muted/30 p-3 text-xs space-y-1">
                <p className="font-medium text-sm mb-2">Password requirements</p>
                <p className={passwordChecks.length ? 'text-green-700' : 'text-muted-foreground'}>
                  {passwordChecks.length ? '✓' : '○'} At least 12 characters
                </p>
                <p className={passwordChecks.lower ? 'text-green-700' : 'text-muted-foreground'}>
                  {passwordChecks.lower ? '✓' : '○'} One lowercase letter
                </p>
                <p className={passwordChecks.upper ? 'text-green-700' : 'text-muted-foreground'}>
                  {passwordChecks.upper ? '✓' : '○'} One uppercase letter
                </p>
                <p className={passwordChecks.number ? 'text-green-700' : 'text-muted-foreground'}>
                  {passwordChecks.number ? '✓' : '○'} One number
                </p>
                <p className={passwordChecks.special ? 'text-green-700' : 'text-muted-foreground'}>
                  {passwordChecks.special ? '✓' : '○'} One special character
                </p>
              </div>

              <Field
                label="Confirm new password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(value) =>
                  setPasswordForm({
                    ...passwordForm,
                    confirm_password: value,
                  })
                }
              />

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordForm({
                      current_password: '',
                      new_password: '',
                      confirm_password: '',
                    });
                  }}
                  disabled={passwordChanging}
                >
                  Cancel
                </Button>

                <Button
                  onClick={savePassword}
                  disabled={
                    passwordChanging ||
                    !passwordForm.current_password ||
                    !passwordForm.new_password ||
                    !passwordForm.confirm_password ||
                    !strongPassword ||
                    passwordForm.new_password !== passwordForm.confirm_password
                  }
                >
                  {passwordChanging ? 'Changing...' : 'Change password'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="sticky top-16 z-30 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/90">
        <nav aria-label="Admin sections" className="max-w-7xl mx-auto px-4 pt-3">
          <div className="flex gap-2 overflow-x-auto pb-3">
          {tabs.map(({ key, label, icon: Icon }) => (
            <Button
              key={key}
              variant={tab === key ? 'default' : 'outline'}
              onClick={() => setTab(key)}
              className="gap-2"
            >
              <Icon className="w-4 h-4" />
              {label}
            </Button>
          ))}
          </div>
        </nav>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {tab === 'Dashboard' && summary && (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-semibold">Financial Summary</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Customer Payments = Farmer Payments + Unpaid Farmer Balance + 0middle Earnings
              </p>
            </div>

            <div className="flex items-stretch gap-2 overflow-x-auto pb-2">
              <div className="min-w-[180px] flex-1">
                <Stat
                  label="Customer Payments"
                  value={`₹${summary.customer_paid || 0}`}
                  note="Confirmed money received"
                  onClick={() => openDashboardDestination('Orders')}
                />
              </div>
              <span className="self-center text-2xl font-semibold text-muted-foreground shrink-0" aria-hidden="true">=</span>
              <div className="min-w-[180px] flex-1">
                <Stat
                  label="Farmer Payments"
                  value={`₹${summary.farmer_paid_total || 0}`}
                  note={`${summary.farmer_paid_orders || 0} orders marked farmer paid`}
                  onClick={() => openDashboardDestination('Orders')}
                />
              </div>
              <span className="self-center text-2xl font-semibold text-muted-foreground shrink-0" aria-hidden="true">+</span>
              <div className="min-w-[180px] flex-1">
                <Stat
                  label="Unpaid Farmer Balance"
                  value={`₹${Math.round(((Number(summary.farmer_payable) || 0) - (Number(summary.farmer_paid_total) || 0)) * 100) / 100}`}
                  note="Owed on confirmed orders, not yet paid"
                  onClick={() => openDashboardDestination('Orders')}
                />
              </div>
              <span className="self-center text-2xl font-semibold text-muted-foreground shrink-0" aria-hidden="true">+</span>
              <div className="min-w-[180px] flex-1">
                <Stat
                  label="0middle Earnings"
                  value={`₹${summary.platform_fee || 0}`}
                  note="Fees from confirmed customer payments"
                  onClick={() => openDashboardDestination('Orders')}
                />
              </div>
            </div>

            <div>
              <div className="flex items-end justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-lg font-semibold">Shipping performance</h2>
                  <p className="text-sm text-muted-foreground">Promise: ship within 72 hours of order</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => openDashboardDestination('Orders')}>
                  Open order tracker
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat
                  label="Shipped on time"
                  value={orders.filter((order) => shippingTiming(order).state === 'on-time').length}
                  note="Shipped within 72 hours"
                  onClick={() => openDashboardDestination('Orders')}
                />
                <Stat
                  label="Total shipped"
                  value={orders.filter((order) => ['on-time', 'late'].includes(shippingTiming(order).state)).length}
                  note="Orders with shipped date"
                  onClick={() => openDashboardDestination('Orders')}
                />
                <Stat
                  label="Deadline approaching"
                  value={orders.filter((order) => shippingTiming(order).state === 'warning').length}
                  note="Unshipped · 48–72 hours"
                  onClick={() => openDashboardDestination('Orders')}
                />
                <Stat
                  label="Overdue"
                  value={orders.filter((order) => shippingTiming(order).state === 'overdue').length}
                  note="Unshipped · over 72 hours"
                  onClick={() => openDashboardDestination('Orders')}
                />
              </div>
            </div>

            <div>
              <div className="flex items-end justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-lg font-semibold">Website traffic</h2>
                  <p className="text-sm text-muted-foreground">
                    Last {customerFunnel?.window_days || 30} days
                  </p>
                  <div className="mt-2 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm">
                    <span className={
                      trafficTrend.direction === 'up'
                        ? 'text-emerald-700 font-semibold'
                        : trafficTrend.direction === 'down'
                          ? 'text-red-700 font-semibold'
                          : 'text-muted-foreground font-semibold'
                    }>
                      {trafficTrend.direction === 'up' ? '↑' : trafficTrend.direction === 'down' ? '↓' : '→'}
                      {' '}
                      {trafficTrend.percent == null
                        ? (trafficTrend.current > 0 ? 'New traffic' : 'No change')
                        : `${Math.abs(trafficTrend.percent)}%`}
                    </span>
                    <span className="text-muted-foreground">
                      visitors · last 7 days vs previous 7 days
                    </span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openDashboardDestination('Funnel')}
                >
                  Open web traffic
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Stat
                  label="Visitors"
                  value={customerFunnel?.visitors ?? '—'}
                  note="Unique browser IDs"
                  onClick={() => openDashboardDestination('Funnel')}
                />
                <Stat
                  label="Checkout started"
                  value={customerFunnel?.checkout_started ?? '—'}
                  note="Entered delivery details"
                  onClick={() => openDashboardDestination('Funnel')}
                />
                <Stat
                  label="Payment screen"
                  value={customerFunnel?.payment_viewed ?? '—'}
                  note="Reached UPI payment"
                  onClick={() => openDashboardDestination('Funnel')}
                />
                <Stat
                  label="Purchase declared"
                  value={customerFunnel?.purchased ?? '—'}
                  note="Tapped I’ve paid"
                  onClick={() => openDashboardDestination('Funnel')}
                />
                <Stat
                  label="Abandoned"
                  value={customerFunnel?.abandoned_count ?? '—'}
                  note="No purchase after 30 min"
                  onClick={() => openDashboardDestination('Funnel')}
                />
              </div>
            </div>

            <DailyOrdersChart orders={orders} />



          </div>
        )}

        {tab === 'Farmers' && (
          <div className="space-y-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold">Farmers</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Add, review and maintain farmer profiles.
                </p>
              </div>
              <Button onClick={exportFarmersCsv}>
                Export CSV
              </Button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <Sprout className="w-5 h-5 text-primary" />
                    <h3 className="text-xl font-semibold">
                      {editingFarmerSlug ? 'Edit farmer' : 'Add farmer'}
                    </h3>
                  </div>

                  <Field
                    label="Farmer name"
                    value={farmerForm.name}
                    onChange={(v) => setFarmerForm({...farmerForm, name:v})}
                  />

                  <div>
                    <label className="text-sm font-medium">Pincode</label>
                    <Input
                      inputMode="numeric"
                      value={farmerForm.pincode}
                      onChange={(e) => handleFarmerPincode(e.target.value)}
                      maxLength={6}
                      className="mt-1"
                    />
                    {pincodeLoading && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Finding location...
                      </p>
                    )}
                  </div>

                  <Field
                    label="Village / Locality"
                    value={farmerForm.village}
                    onChange={(v) => setFarmerForm({...farmerForm, village:v})}
                  />
                  <Field
                    label="City"
                    value={farmerForm.city}
                    onChange={(v) => setFarmerForm({...farmerForm, city:v})}
                  />
                  <Field
                    label="District"
                    value={farmerForm.district}
                    onChange={(v) => setFarmerForm({...farmerForm, district:v})}
                  />
                  <Field
                    label="State"
                    value={farmerForm.state}
                    onChange={(v) => setFarmerForm({...farmerForm, state:v})}
                  />

                  <div>
                    <label className="text-sm font-medium">Address</label>
                    <textarea
                      value={farmerForm.address}
                      onChange={(e) => setFarmerForm({...farmerForm, address:e.target.value})}
                      className="w-full min-h-[88px] border rounded-md px-3 py-2 bg-background mt-1"
                    />
                  </div>

                  <Field
                    label="FSSAI"
                    value={farmerForm.fssai}
                    onChange={(v) => setFarmerForm({...farmerForm, fssai:v})}
                  />

                  <div>
                    <label className="text-sm font-medium">Phone Number</label>
                    <Input
                      inputMode="numeric"
                      value={farmerForm.phone}
                      onChange={(e) =>
                        setFarmerForm({
                          ...farmerForm,
                          phone: e.target.value.replace(/\D/g, ''),
                        })
                      }
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium">Farmer photos</label>
                    <p className="text-xs text-muted-foreground mt-1">Upload up to 2 photos. The first photo is the primary farmer photo.</p>
                    <div className="mt-2 flex flex-wrap gap-3">
                      {(farmerForm.photo_urls?.length ? farmerForm.photo_urls : (farmerForm.photo_url ? [farmerForm.photo_url] : [])).map((photoUrl, index) => (
                        <div key={photoUrl + "-" + index} className="relative">
                          <img src={photoUrl} alt={"Farmer preview " + (index + 1)} className="w-24 h-24 rounded-lg object-cover border" />
                          <button type="button" onClick={() => removeFarmerPhoto(index)} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-background border shadow flex items-center justify-center" aria-label={"Remove farmer photo " + (index + 1)}>×</button>
                          <p className="text-[11px] text-center mt-1">{index === 0 ? "Primary" : "Photo " + (index + 1)}</p>
                        </div>
                      ))}
                    </div>
                    {(farmerForm.photo_urls?.length || (farmerForm.photo_url ? 1 : 0)) < 2 && (
                      <Input type="file" accept="image/*" className="mt-2" onChange={(e) => { handleFarmerPhotoUpload(e.target.files?.[0]); e.target.value = ""; }} />
                    )}
                    <p className="text-xs text-muted-foreground mt-1">{farmerForm.photo_urls?.length || (farmerForm.photo_url ? 1 : 0)} / 2 photos</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Farmer headline</label>
                    <Input
                      value={farmerForm.headline}
                      onChange={(e) => setFarmerForm({...farmerForm, headline:e.target.value})}
                      spellCheck
                      className="mt-1"
                      placeholder="40 years of caring for her farm."
                    />
                    <p className={`text-xs mt-1 ${countWords(farmerForm.headline) > 10 ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {countWords(farmerForm.headline)} / 10 words
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Farmer description</label>
                    <textarea
                      value={farmerForm.story}
                      onChange={(e) => setFarmerForm({...farmerForm, story:e.target.value})}
                      spellCheck
                      className="w-full min-h-[160px] border rounded-md px-3 py-2 bg-background mt-1"
                      placeholder="Write a short farmer story..."
                    />
                    <p className={`text-xs mt-1 ${countWords(farmerForm.story) > 120 ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {countWords(farmerForm.story)} / 120 words
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={saveFarmer} disabled={!farmerForm.name}>
                      {editingFarmerSlug ? 'Save changes' : 'Add farmer'}
                    </Button>
                    {editingFarmerSlug && (
                      <Button variant="outline" onClick={resetFarmerForm}>
                        Cancel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-3">
                {farmers.length === 0 && (
                  <Card>
                    <CardContent className="p-4 text-muted-foreground">
                      No farmers added yet.
                    </CardContent>
                  </Card>
                )}

                {farmers.map((farmer) => (
                  <Card
                    key={farmer.slug}
                    className="cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => setSelectedFarmer(farmer)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold">{farmer.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {[farmer.city || farmer.village, farmer.district, farmer.state]
                              .filter(Boolean)
                              .join(', ') || 'Location not entered'}
                          </p>
                          {farmer.pincode && (
                            <p className="text-xs text-muted-foreground mt-1">
                              PIN {farmer.pincode}
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => editFarmer(farmer)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => removeFarmer(farmer)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {selectedFarmer && (
                  <Card>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-semibold">{selectedFarmer.name}</h3>
                          <p className="text-xs text-muted-foreground mt-1">
                            Farmer profile details
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedFarmer(null)}
                        >
                          Close
                        </Button>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-x-5 gap-y-3 mt-4 text-sm">
                        <FarmerDetail label="Pincode" value={selectedFarmer.pincode} />
                        <FarmerDetail label="Village / Locality" value={selectedFarmer.village} />
                        <FarmerDetail label="City" value={selectedFarmer.city} />
                        <FarmerDetail label="District" value={selectedFarmer.district} />
                        <FarmerDetail label="State" value={selectedFarmer.state} />
                        <FarmerDetail label="FSSAI" value={selectedFarmer.fssai} />
                        <FarmerDetail label="Phone Number" value={selectedFarmer.phone} />
                        <div className="sm:col-span-2">
                          <FarmerDetail label="Address" value={selectedFarmer.address} />
                        </div>
                        <div className="sm:col-span-2">
                          <FarmerDetail label="Headline" value={selectedFarmer.headline} />
                        </div>
                        <div className="sm:col-span-2">
                          <FarmerDetail label="Story" value={selectedFarmer.story} />
                        </div>
                        {(selectedFarmer.photo_urls?.length || selectedFarmer.photo_url) && (
                          <div className="sm:col-span-2">
                            <p className="text-xs text-muted-foreground mb-1">Farmer photos</p>
                            <div className="flex flex-wrap gap-2">
                              {(selectedFarmer.photo_urls?.length ? selectedFarmer.photo_urls : [selectedFarmer.photo_url]).filter(Boolean).slice(0, 2).map((photoUrl, index) => (
                                <img key={photoUrl + "-" + index} src={photoUrl} alt={selectedFarmer.name + " " + (index + 1)} className="w-32 h-32 rounded-lg object-cover border" />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === 'Products' && (
          <div className="space-y-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold">Products</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Create, post, edit and manage all product listings.
                </p>
              </div>
              <Button onClick={exportProductsCsv}>
                Export CSV
              </Button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <PackagePlus className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-semibold">
                    {editingProductSlug ? 'Edit product' : 'Add new product'}
                  </h2>
                </div>
                <p className="text-sm text-muted-foreground">
                  Set the pack size, price, starting inventory and product image.
                </p>

                <label className="text-sm font-medium">Farmer</label>
                <select
                  className="w-full border rounded-md h-10 px-3 bg-background"
                  value={productForm.farmer_slug}
                  onChange={(e) => setProductForm({...productForm, farmer_slug:e.target.value})}
                >
                  <option value="">Select farmer</option>
                  {farmers.map((farmer) => (
                    <option key={farmer.slug} value={farmer.slug}>{farmer.name}</option>
                  ))}
                </select>

                <div>
                  <label className="text-sm font-medium">Product Name</label>
                  <div className="grid grid-cols-[1fr_auto_auto] gap-2 mt-1">
                    <select
                      className="w-full border rounded-md h-10 px-3 bg-background"
                      value={editingProductSlug}
                      onChange={(e) => selectProductForEdit(e.target.value)}
                    >
                      <option value="">+ Enter new product</option>
                      {products.map((product) => (
                        <option key={product.slug} value={product.slug}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!editingProductSlug}
                      onClick={() => selectProductForEdit(editingProductSlug)}
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={!editingProductSlug}
                      onClick={() => {
                        const product = products.find((item) => item.slug === editingProductSlug);
                        if (product) removeProduct(product);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                {!editingProductSlug && (
                  <Field
                    label="New Product Name"
                    value={productForm.name}
                    onChange={(v) => setProductForm({...productForm, name:v})}
                  />
                )}

                {editingProductSlug && (
                  <Field
                    label="Product Name"
                    value={productForm.name}
                    onChange={(v) => setProductForm({...productForm, name:v})}
                  />
                )}

                <Field label="Product Highlights" value={productForm.description} onChange={(v) => setProductForm({...productForm, description:v})} />
                <div className="space-y-3 rounded-lg border p-4">
                  <h3 className="font-semibold">Regional Availability</h3>
                  <label className="flex items-center gap-2"><input type="radio" name="product-availability" checked={productForm.general_availability !== false} onChange={() => setProductForm({...productForm, general_availability: true, available_states: []})} />General Availability (all Indian states and UTs)</label>
                  <label className="flex items-center gap-2"><input type="radio" name="product-availability" checked={productForm.general_availability === false} onChange={() => setProductForm({...productForm, general_availability: false})} />Selected states and UTs</label>
                  {productForm.general_availability === false && <div className="space-y-2">
                    <details className="rounded-md border p-3">
                      <summary className="cursor-pointer text-sm font-medium">Choose states and UTs ({(productForm.available_states || []).length} selected)</summary>
                      <div className="mt-3 max-h-56 overflow-y-auto space-y-2">
                        {INDIAN_STATES_UTS.map(state => <label key={state} className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={(productForm.available_states || []).includes(state)}
                            onChange={e => setProductForm(previous => ({...previous, available_states: e.target.checked ? [...(previous.available_states || []), state] : (previous.available_states || []).filter(item => item !== state)}))} />
                          {state}
                        </label>)}
                      </div>
                    </details>
                  </div>}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="font-semibold">Pack sizes and pricing</h3>
                      <p className="text-xs text-muted-foreground">
                        Add one row for each denomination. Use +, -, *, / and parentheses directly in pricing boxes. Click the comment icon to add a note; notes appear on hover.
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={addProductVariant}
                      className="bg-amber-700 text-white hover:bg-amber-800 border-amber-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add pack size
                    </Button>
                  </div>

                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm min-w-[1050px]">
                      <thead className="bg-muted/40 border-b">
                        <tr>
                          <th className="p-2 text-left">Pack size</th>
                          <th className="p-2 text-left">Unit</th>
                          <th className="p-2 text-left">Farmer ₹ (A)</th>
                          <th className="p-2 text-left">Packaging ₹ (B)</th>
                          <th className="p-2 text-left">Shipping ₹ (C)</th>
                          <th className="p-2 text-left">Testing ₹ (D)</th>
                          <th className="p-2 text-left">Total ₹ (E)</th>
                          <th className="p-2 text-left">Platform ₹ (F)</th>
                          <th className="p-2 text-left">Fee discount ₹</th>
                          <th className="p-2 text-left">List ₹ (G)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productForm.variants.map((variant, index) => {
                          const totalPrice =
                            pricingValue(variant, 'farmer_price') +
                            pricingValue(variant, 'packaging') +
                            pricingValue(variant, 'shipping') +
                            pricingValue(variant, 'testing');
                          const listPrice =
                            totalPrice + pricingValue(variant, 'platform_fee') - pricingValue(variant, 'platform_fee_discount');

                          return (
                            <tr key={`variant-${index}`} className="border-b last:border-b-0 align-top">
                              <td className="p-2">
                                <div className="flex items-center gap-2">
                                  <Input type="number" value={variant.pack_size_value} onChange={(e) => updateProductVariant(index, 'pack_size_value', e.target.value)} className="w-24" />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="shrink-0 text-destructive hover:text-destructive"
                                    disabled={productForm.variants.length === 1}
                                    title={productForm.variants.length === 1 ? 'A product must have at least one pack size' : 'Delete this pack size'}
                                    aria-label={`Delete ${variant.pack_size_value || ''} ${variant.pack_size_unit || ''} pack size`}
                                    onClick={() => {
                                      const size = `${variant.pack_size_value || ''} ${variant.pack_size_unit || ''}`.trim();
                                      if (window.confirm(`Delete the ${size} pack size from this product? Save the product to apply this change.`)) {
                                        removeProductVariant(index);
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                              <td className="p-2">
                                <select className="border rounded-md h-10 px-2 bg-background" value={variant.pack_size_unit} onChange={(e) => updateProductVariant(index, 'pack_size_unit', e.target.value)}>
                                  <option value="ml">ml</option>
                                  <option value="L">L</option>
                                  <option value="mg">mg</option>
                                  <option value="g">g</option>
                                  <option value="kg">kg</option>
                                  <option value="count">count</option>
                                </select>
                              </td>
                              <td className="p-2">{renderPricingCell(variant, index, 'farmer_price')}</td>
                              <td className="p-2">{renderPricingCell(variant, index, 'packaging')}</td>
                              <td className="p-2">{renderPricingCell(variant, index, 'shipping')}</td>
                              <td className="p-2">{renderPricingCell(variant, index, 'testing')}</td>
                              <td className="p-2"><Input value={totalPrice} readOnly className="w-24 bg-muted/40 font-semibold" /></td>
                              <td className="p-2">{renderPricingCell(variant, index, 'platform_fee')}</td>
                              <td className="p-2">{renderPricingCell(variant, index, 'platform_fee_discount')}</td>
                              <td className="p-2"><Input value={listPrice} readOnly className="w-24 bg-muted/40 font-semibold" /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Product images</label>

                  <div className="mt-1 border rounded-lg p-3 space-y-3">
                    {productForm.images.length ? (
                      <div className="flex flex-wrap gap-3">
                        {productForm.images.map((image, index) => {
                          const isSelected = selectedProductImageIndex === index;

                          return (
                            <div
                              key={`${image.url}-${index}`}
                              className={`space-y-2 rounded-xl p-2 cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-2 border-primary bg-primary/5 shadow-md'
                                  : 'border border-transparent'
                              }`}
                              onClick={() => setSelectedProductImageIndex(index)}
                            >
                              <div className="relative">
                                <img
                                  src={image.url}
                                  alt={`Product preview ${index + 1}`}
                                  className={`w-32 h-32 object-contain rounded-lg bg-white ${
                                    isSelected ? 'ring-2 ring-primary ring-offset-2' : 'border'
                                  }`}
                                />
                                {index === 0 && (
                                  <Badge className="absolute left-1 top-1">
                                    Primary
                                  </Badge>
                                )}
                                {isSelected && index !== 0 && (
                                  <Badge
                                    variant="outline"
                                    className="absolute right-1 top-1 bg-white text-primary border-primary"
                                  >
                                    Selected
                                  </Badge>
                                )}
                              </div>

                              <div className="flex gap-2">
                                {index !== 0 && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setSelectedProductImageIndex(index);
                                      setPrimaryProductImage(index);
                                    }}
                                    disabled={imageUploading}
                                  >
                                    Make primary
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    removeProductFormImage(index);
                                  }}
                                  disabled={imageUploading}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="w-32 h-32 rounded-lg border border-dashed flex items-center justify-center text-xs text-muted-foreground text-center px-3">
                        No images uploaded
                      </div>
                    )}

                    <div className="space-y-1">
                      <Input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        disabled={imageUploading}
                        onChange={(e) => {
                          uploadProductImages(e.target.files);
                          e.target.value = '';
                        }}
                      />
                      <p className="text-xs text-muted-foreground">
                        Select one image or multiple images at once. You can use this picker again to add images individually.
                      </p>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      JPG, PNG or WebP · maximum 10 MB per image. Images are resized and optimized automatically. The first image is the primary image.
                    </p>

                    {imageUploading && (
                      <p className="text-sm font-medium">
                        Uploading image{productForm.images.length ? 's' : ''}...
                      </p>
                    )}
                  </div>
                </div>

                <div className="mb-4 rounded-md border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">Saved product drafts</span>
                    <Button type="button" variant="outline" size="sm" onClick={refreshProductDrafts}>Refresh drafts</Button>
                  </div>
                  {productDrafts.map((draft) => (
                    <div key={draft.draft_id} className="flex items-center justify-between gap-2 text-sm">
                      <span className="truncate">{draft.form?.name || 'Untitled product'} — {draft.updated_at ? new Date(draft.updated_at).toLocaleString() : ''}</span>
                      <div className="flex gap-1">
                        <Button type="button" size="sm" variant="outline" onClick={() => {
                          setProductForm(draft.form);
                          setCurrentDraftId(draft.draft_id);
                          setEditingProductSlug('');
                          toast.success('Draft loaded. Continue editing.');
                        }}>Resume</Button>
                        <Button type="button" size="sm" variant="outline" onClick={async () => {
                          if (!window.confirm('Delete this saved draft?')) return;
                          try {
                            await deleteAdminProductDraft(draft.draft_id);
                            if (currentDraftId === draft.draft_id) setCurrentDraftId(null);
                            await refreshProductDrafts();
                          } catch { toast.error('Unable to delete draft'); }
                        }}>Delete</Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button type="button" variant="outline" onClick={saveDraft} disabled={savingDraft}>
                    {savingDraft ? 'Saving...' : 'Save Draft'}
                  </Button>
                  <Button
                    onClick={saveProduct}
                    className={editingProductSlug && products.some((product) => product.slug === editingProductSlug && product.active) ? 'bg-green-900 text-white hover:bg-green-800' : ''}
                    disabled={!productForm.farmer_slug || !productForm.name?.trim() || !productForm.pack_size_value}
                  >
                    {editingProductSlug
                      ? (products.some((product) => product.slug === editingProductSlug && product.active) ? 'Save & Post' : 'Save changes')
                      : 'Add Product'}
                  </Button>
                  {editingProductSlug && (
                    <>
                      <Button type="button" variant="outline" onClick={resetProductForm}>
                        Cancel
                      </Button>
                      <Button type="button" variant="outline" onClick={resetProductForm}>
                        New product
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              {[...products]
                .sort((a, b) => {
                  if (Boolean(a.active) !== Boolean(b.active)) return a.active ? -1 : 1;
                  const aTime = Date.parse(a.created_at || a.updated_at || '') || 0;
                  const bTime = Date.parse(b.created_at || b.updated_at || '') || 0;
                  return bTime - aTime || String(b.batch_number || '').localeCompare(String(a.batch_number || ''));
                })
                .map((product) => (
                <Card key={product.slug}>
                  <CardContent className="p-4">
                    <div className="flex justify-between gap-3">
                      <div>
                        <p className="font-semibold">{product.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {product.pack_size} · {
                            farmers.find((farmer) => farmer.slug === product.farmer_slug)?.name ||
                            product.farmer_slug
                          }
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Batch: <span className="font-medium text-foreground">{product.batch_number || '—'}</span>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Availability: <span className="font-medium text-foreground">
                            {product.general_availability !== false
                              ? 'General Availability — All Indian states and UTs'
                              : (product.available_states?.length
                                  ? product.available_states.join(', ')
                                  : 'No states or UTs selected')}
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={product.active ? undefined : "bg-amber-800 text-white border-amber-800 hover:bg-amber-800"}>{product.active ? 'Posted' : 'Unposted'}</Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => selectProductForEdit(product.slug)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={copyingProductSlug === product.slug}
                          onClick={() => copyProduct(product)}
                          title="Create a separate unposted batch with fresh inventory"
                        >
                          {copyingProductSlug === product.slug ? 'Creating...' : 'Create new batch'}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => removeProduct(product)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 grid gap-4 md:grid-cols-[auto_1fr] items-start">
                      {product.image_url && (
                        <div className="space-y-2">
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-16 h-16 rounded-md object-cover border"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => removeExistingProductImage(product.slug)}
                          >
                            Remove image
                          </Button>
                        </div>
                      )}

                      <div className="min-w-0">
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <h4 className="text-sm font-semibold">Pack sizes and cost split</h4>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => saveCardPricing(product)}
                              disabled={
                                !cardPricingState(product).dirty ||
                                savingCardPricing === product.slug
                              }
                            >
                              {savingCardPricing === product.slug ? 'Saving...' : 'Save'}
                            </Button>
                          </div>
                        </div>

                        <div className="overflow-x-auto border rounded-lg">
                          <table className="w-max min-w-full table-auto text-sm">
                            <thead className="bg-muted/40 border-b">
                              <tr>
                                <th className="text-left p-2 font-semibold">Cost split</th>
                                {cardPricingState(product).variants.map((variant, variantIndex) => (
                                  <th
                                    key={variant.variant_id || `${product.slug}-head-${variantIndex}`}
                                    className="text-left p-2 font-semibold"
                                  >
                                    {variant.pack_size ||
                                      `${variant.pack_size_value ?? ''} ${variant.pack_size_unit ?? ''}`.trim() ||
                                      product.pack_size}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {[
                                ['Farmer ₹ (A)', 'farmer_price'],
                                ['Packaging ₹ (B)', 'packaging'],
                                ['Shipping ₹ (C)', 'shipping'],
                                ['Testing ₹ (D)', 'testing'],
                              ].map(([label, field]) => (
                                <tr key={field} className="border-b">
                                  <td className="p-2 font-medium bg-muted/20 whitespace-nowrap">{label}</td>
                                  {cardPricingState(product).variants.map((variant, variantIndex) => (
                                    <td
                                      key={variant.variant_id || `${product.slug}-${field}-${variantIndex}`}
                                      className="p-2 text-left"
                                    >
                                      <Input
                                        type="number"
                                        inputMode="decimal"
                                        step="0.01"
                                        min="0"
                                        value={variant[field]}
                                        onChange={(event) =>
                                          updateCardPricing(
                                            product,
                                            variantIndex,
                                            field,
                                            event.target.value
                                          )
                                        }
                                        className="h-8 w-[9ch] min-w-[76px] max-w-[120px] text-left [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}

                              <tr className="border-b bg-muted/10">
                                <td className="p-2 font-semibold whitespace-nowrap">Total ₹ (E)</td>
                                {cardPricingState(product).variants.map((variant, variantIndex) => {
                                  const total =
                                    Number(variant.farmer_price || 0) +
                                    Number(variant.packaging || 0) +
                                    Number(variant.shipping || 0) +
                                    Number(variant.testing || 0);
                                  return (
                                    <td
                                      key={variant.variant_id || `${product.slug}-total-${variantIndex}`}
                                      className="p-2 text-left font-semibold"
                                    >
                                      ₹{Math.round(total * 100) / 100}
                                    </td>
                                  );
                                })}
                              </tr>

                              <tr className="border-b">
                                <td className="p-2 font-medium bg-muted/20 whitespace-nowrap">Platform ₹ (F)</td>
                                {cardPricingState(product).variants.map((variant, variantIndex) => (
                                  <td
                                    key={variant.variant_id || `${product.slug}-platform-${variantIndex}`}
                                    className="p-2"
                                  >
                                    <Input
                                      type="number"
                                      inputMode="decimal"
                                      step="0.01"
                                      min="0"
                                      value={variant.platform_fee}
                                      onChange={(event) =>
                                        updateCardPricing(
                                          product,
                                          variantIndex,
                                          'platform_fee',
                                          event.target.value
                                        )
                                      }
                                      className="h-8 w-[9ch] min-w-[76px] max-w-[120px] text-left [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                    />
                                  </td>
                                ))}
                              </tr>

                              <tr className="border-b">
                                <td className="p-2 font-medium bg-muted/20 whitespace-nowrap">Platform fee discount ₹</td>
                                {cardPricingState(product).variants.map((variant, index) => (
                                  <td key={index} className="p-2">
                                    <Input type="number" step="0.01" min="0" max={Number(variant.platform_fee || 0)}
                                      value={variant.platform_fee_discount ?? '0'}
                                      onChange={(event) => updateCardPricing(product, index, 'platform_fee_discount', event.target.value)}
                                      className="h-8 w-[9ch] min-w-[76px] max-w-[120px]" />
                                  </td>
                                ))}
                              </tr>
                              <tr className="border-b">
                                <td className="p-2 font-semibold">Net 0middle fee</td>
                                {cardPricingState(product).variants.map((variant, index) => (
                                  <td key={index} className="p-2 font-semibold">
                                    ₹{Math.max(0, Number(variant.platform_fee || 0) - Number(variant.platform_fee_discount || 0))}
                                  </td>
                                ))}
                              </tr>
                              <tr className="border-b">
                                <td className="p-2 font-medium bg-muted/20">0middle fee discount ₹</td>
                                {cardPricingState(product).variants.map((variant, variantIndex) => (
                                  <td key={variant.variant_id || variantIndex} className="p-2">
                                    <Input type="number" min="0" step="0.01"
                                      max={Number(variant.platform_fee || 0)}
                                      value={variant.platform_fee_discount ?? '0'}
                                      onChange={(event) => updateCardPricing(product, variantIndex, 'platform_fee_discount', event.target.value)}
                                      className="h-8 w-[9ch] min-w-[76px]" />
                                  </td>
                                ))}
                              </tr>
                              <tr className="border-b">
                                <td className="p-2 font-semibold">Net platform fee</td>
                                {cardPricingState(product).variants.map((variant, variantIndex) => (
                                  <td key={variant.variant_id || variantIndex} className="p-2">
                                    ₹{Number(variant.platform_fee || 0) - Number(variant.platform_fee_discount || 0)}
                                  </td>
                                ))}
                              </tr>
                              <tr className="bg-muted/10">
                                <td className="p-2 font-semibold whitespace-nowrap">List ₹ (G)</td>
                                {cardPricingState(product).variants.map((variant, variantIndex) => {
                                  const total =
                                    Number(variant.farmer_price || 0) +
                                    Number(variant.packaging || 0) +
                                    Number(variant.shipping || 0) +
                                    Number(variant.testing || 0);
                                  const list = total + Number(variant.platform_fee || 0) - Number(variant.platform_fee_discount || 0);
                                  return (
                                    <td
                                      key={variant.variant_id || `${product.slug}-list-${variantIndex}`}
                                      className="p-2 text-left font-semibold"
                                    >
                                      ₹{Math.round(list * 100) / 100}
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {cardPricingState(product).dirty && (
                          <p className="text-xs text-amber-700 mt-2">
                            Unsaved pricing changes. Save first; then Post becomes available.
                          </p>
                        )}

                        <label className="inline-flex mt-3">
                          <span className="sr-only">Replace product image</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            className="text-sm"
                            disabled={replacingImageFor === product.slug}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              replaceProductImage(product.slug, file);
                              e.target.value = '';
                            }}
                          />
                        </label>

                        {replacingImageFor === product.slug && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Uploading...
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            </div>
          </div>
        )}

        {tab === 'Inventory' && (
          <div className="space-y-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold">Inventory Profile</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  New products and batches remain unposted until you enter inventory and click POST.
                </p>
              </div>
              <Button onClick={exportInventoryCsv}>
                Export CSV
              </Button>
            </div>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      <th className="text-left p-3 font-semibold">Product Name</th>
                      <th className="text-left p-3 font-semibold">Farmer Name</th>
                      <th className="text-left p-3 font-semibold">Batch Number</th>
                      <th className="text-left p-3 font-semibold">Opened (date / admin)</th>
                      <th className="text-left p-3 font-semibold">Closed (date / admin)</th>
                      <th className="text-left p-3 font-semibold">Post / Unpost</th>
                      <th className="text-left p-3 font-semibold">Original Available Quantity</th>
                      <th className="text-left p-3 font-semibold">Ordered Quantity</th>
                      <th className="text-left p-3 font-semibold">Remaining Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryProfile.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="p-4 text-muted-foreground">
                          No inventory records yet.
                        </td>
                      </tr>
                    ) : (
                      inventoryProfile.map((row) => (
                        <tr key={row.product_slug} className={`border-b last:border-b-0 ${Number(row.remaining_quantity ?? 0) <= 0 && Number(row.original_available_quantity ?? 0) > 0 ? "bg-gray-100 text-gray-500" : ""}`}>
                          <td className="p-3">{row.product_name || '—'}</td>
                          <td className="p-3">{row.farmer_name || '—'}</td>
                          <td className="p-3 font-medium">
                            <div className="flex items-center gap-2">
                              <span>{row.batch_number || '—'}</span>
                              {Number(row.original_available_quantity ?? 0) === 0 && Number(row.ordered_quantity ?? 0) === 0 && (
                                <Badge variant="outline" className="border-primary/40 text-primary text-[10px] font-semibold">NEW</Badge>
                              )}
                              {Number(row.original_available_quantity ?? 0) > 0 && Number(row.remaining_quantity ?? 0) <= 0 && (
                                <Badge variant="outline" className="border-gray-400 text-gray-600 text-[10px] font-semibold whitespace-nowrap">BATCH CLOSED</Badge>
                              )}
                            </div>
                          </td>
                          <td className="p-3 text-xs">
                            {row.batch_opened_at ? <><div>{new Date(row.batch_opened_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</div><div>{row.batch_opened_by || 'Unknown admin'}</div></> : '—'}
                          </td>
                          <td className="p-3 text-xs">
                            {row.batch_closed_at ? <><div>{new Date(row.batch_closed_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</div><div>{row.batch_closed_by || 'Unknown'}</div></> : '—'}
                          </td>
                          <td className="p-3">
                            <Button
                              size="sm"
                              variant={row.active ? 'outline' : 'default'}
                              disabled={postingInventorySlug === row.product_slug || (!row.active && Number(row.remaining_quantity ?? 0) <= 0)}
                              onClick={() => setInventoryPosted(row)}
                            >
                              {postingInventorySlug === row.product_slug ? 'Saving...' : row.active ? 'UNPOST' : 'POST'}
                            </Button>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={row.ordered_quantity ?? 0}
                                step="any"
                                disabled={Boolean(row.active) || (Number(row.original_available_quantity ?? 0) > 0 && Number(row.remaining_quantity ?? 0) <= 0)}
                                className="w-28 h-9 bg-background text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                                aria-label={`Original quantity for ${row.product_name}, batch ${row.batch_number}`}
                                value={inventoryDrafts[row.product_slug] ?? String(row.original_available_quantity ?? 0)}
                                onChange={(event) => setInventoryDrafts((previous) => ({
                                  ...previous,
                                  [row.product_slug]: event.target.value,
                                }))}
                              />
                              <span>{row.unit || ''}</span>
                              {inventoryDrafts[row.product_slug] !== undefined &&
                                Number(inventoryDrafts[row.product_slug]) !== Number(row.original_available_quantity ?? 0) && (
                                  <Button
                                    size="sm"
                                    disabled={savingInventorySlug === row.product_slug}
                                    onClick={() => saveInventoryQuantity(row)}
                                  >
                                    Save
                                  </Button>
                                )}
                            </div>
                          </td>
                          <td className="p-3">
                            {row.ordered_quantity ?? 0} {row.unit || ''}
                          </td>
                          <td className="p-3">
                            {row.remaining_quantity ?? 0} {row.unit || ''}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

          </div>
        )}

        {tab === 'Orders' && (
          <div className="space-y-4">
            <Card>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-semibold">
                      <span className="font-sans">0</span>middle Operations Assistant
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Ask about today’s actions, order highlights, or customer behavior.
                    </p>
                  </div>
                  <Badge variant="outline">Live data</Badge>
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  {['Things to do today', 'Ordering highlights', 'Customer behavior'].map((prompt) => (
                    <button
                      type="button"
                      key={prompt}
                      className="text-xs border rounded-full px-3 py-1.5 hover:bg-muted"
                      onClick={() => {
                        setAssistantQuery(prompt);
                        runOperationsAssistant(prompt);
                      }}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>

                <div ref={assistantScrollRef} className="mt-4 max-h-[560px] min-h-[160px] overflow-y-auto overscroll-contain rounded-xl bg-muted/20 p-3" aria-label="Operations assistant conversation">
                {assistantConversation.map((turn, turnIndex) => (
                  <div key={turnIndex} className="mt-3 border rounded-xl p-4 space-y-3 bg-white">
                    <div className="flex justify-end"><div className="rounded-2xl bg-muted px-4 py-2 text-sm max-w-[85%] whitespace-pre-wrap">{turn.question}</div></div><h3 className="font-semibold">Answer</h3>
                    <div className="text-base whitespace-pre-wrap break-words leading-relaxed">{turn.answer.answer}</div>
                    <p className="text-xs text-muted-foreground">Question interpretation: {turn.answer.method || 'Not available'}</p>
                    {turn.answer.steps?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">How this was calculated</h4>
                        <ol className="list-decimal pl-5 space-y-1 text-sm">
                          {turn.answer.steps.map((step, index) => <li key={index}>{step}</li>)}
                        </ol>
                      </div>
                    )}
                    {turn.answer.customers?.length > 0 && (
                      <div className="mt-3">
                        <h4 className="font-medium text-sm mb-2">Customers from Order Tracker</h4>
                        <ul className="list-disc pl-5 text-sm space-y-1">
                          {turn.answer.customers.map((customer, index) => (
                            <li key={index}>{customer.name} ({customer.order_ids?.length || 0} order(s))</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {turn.answer.orders?.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="text-sm w-full">
                          <thead><tr className="border-b text-left"><th className="py-2">Order</th><th>Weight (kg)</th><th>Shipping (₹)</th><th>Origin source</th></tr></thead>
                          <tbody>{turn.answer.orders.map((order, index) => (
                            <tr key={order.order_id || index} className="border-b">
                              <td className="py-2">{order.order_id}</td><td>{order.weight_kg}</td><td>{order.cost_inr}</td><td>{order.origin_source}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                    )}
                    {turn.answer.excluded?.length > 0 && <p className="text-sm text-muted-foreground">Excluded orders: {turn.answer.excluded.map((o) => o.order_id).join(', ')}</p>}
                    {turn.answer.caveat && <p className="text-sm text-muted-foreground">{turn.answer.caveat}</p>}
                  </div>
                ))}

                  {assistantBusy && <p className="text-sm text-muted-foreground p-3">Analyzing your question…</p>}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <Input
                    value={assistantQuery}
                    onChange={(event) => setAssistantQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') runOperationsAssistant();
                    }}
                    placeholder='Ask a follow-up question…'
                    className="flex-1"
                  />
                  <Button onClick={() => runOperationsAssistant()} disabled={assistantBusy}>
                    {assistantBusy ? 'Analyzing…' : 'Ask'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetOperationsAssistant}>Reset</Button>
                </div>

                {assistantAction && (
                  <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onMouseDown={(event) => { if (event.target === event.currentTarget && !assistantActionBusy) setAssistantAction(null); }}>
                    <div role="dialog" aria-modal="true" aria-label={assistantAction.title} className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="text-xl font-semibold">{assistantAction.title}</h3>
                        <Button type="button" variant="outline" disabled={assistantActionBusy} onClick={() => setAssistantAction(null)}>Close</Button>
                      </div>
                      <p className="text-sm text-muted-foreground">{assistantAction.detail}</p>
                      {assistantAction.orderIds?.map((id) => {
                        const order = orders.find((item) => item.order_id === id);
                        if (!order) return <p key={id}>Order {id} is no longer available. Refresh the dashboard.</p>;
                        const draft = assistantAction.drafts?.[id] || {};
                        const edit = (field, value) => setAssistantAction((previous) => ({ ...previous, drafts: { ...previous.drafts, [id]: { ...(previous.drafts?.[id] || {}), [field]: value } } }));
                        return (
                          <div key={id} className="border rounded-xl p-4 space-y-3">
                            <div className="font-semibold">{id} · {order.customer?.name || 'Customer'}</div>
                            <div className="text-sm">{order.item?.name || 'Product'} · ₹{Number(order.pricing?.total || 0).toLocaleString('en-IN')}</div>
                            {assistantAction.kind === 'payment' && (
                              <Button disabled={assistantActionBusy || order.payment_status === 'PAYMENT_CONFIRMED'} onClick={async () => {
                                if (!window.confirm(`Confirm you verified receipt of ₹${Number(order.pricing?.total || 0).toLocaleString('en-IN')} for order ${id} in the bank/UPI account?`)) return;
                                setAssistantActionBusy(true);
                                try { await confirmAdminPayment(id); toast.success(`Payment confirmed: ${id}`); await load(); setAssistantAction(null); }
                                catch (error) { toast.error(error?.response?.data?.detail || 'Payment confirmation failed'); }
                                finally { setAssistantActionBusy(false); }
                              }}>Confirm verified payment</Button>
                            )}
                            {assistantAction.kind === 'customer_communication' && (
                              <div className="space-y-2">
                                <p className="text-sm">Shipped: {order.shipped_date || 'Recorded'} · Communicated: {order.customer_communicated ? 'Yes' : 'No'}</p>
                                <Button disabled={assistantActionBusy || Boolean(order.customer_communicated)} onClick={async () => {
                                  setAssistantActionBusy(true);
                                  try { await updateAdminOrderTracker(id, { customer_communicated: true }); toast.success('Customer communication recorded'); await load(); setAssistantAction(null); }
                                  catch (error) { toast.error(error?.response?.data?.detail || 'Unable to record communication'); }
                                  finally { setAssistantActionBusy(false); }
                                }}>Mark customer communicated</Button>
                              </div>
                            )}
                            {assistantAction.kind === 'delivery_followup' && (
                              <div className="space-y-3">
                                <label className="block text-sm">Customer received date (if delivered)
                                  <Input type="date" value={draft.customer_received_date ?? order.customer_received_date ?? ''} onChange={(event) => edit('customer_received_date', event.target.value)} />
                                </label>
                                <label className="block text-sm">Tracking update / customer follow-up notes
                                  <Input value={draft.customer_remarks ?? order.customer_remarks ?? ''} onChange={(event) => edit('customer_remarks', event.target.value)} placeholder="e.g. Courier confirms on track, expected tomorrow" />
                                </label>
                                <Button disabled={assistantActionBusy || !(draft.customer_received_date || draft.customer_remarks?.trim())} onClick={async () => {
                                  setAssistantActionBusy(true);
                                  try {
                                    await updateAdminOrderTracker(id, { ...(draft.customer_received_date ? { customer_received_date: draft.customer_received_date } : {}), ...(draft.customer_remarks?.trim() ? { customer_remarks: draft.customer_remarks.trim() } : {}) });
                                    toast.success('Delivery follow-up recorded');
                                    await load(); setAssistantAction(null);
                                  } catch (error) { toast.error(error?.response?.data?.detail || 'Unable to save delivery follow-up'); }
                                  finally { setAssistantActionBusy(false); }
                                }}>Save delivery follow-up</Button>
                              </div>
                            )}
                            {assistantAction.kind === 'shipping' && (
                              <div className="space-y-3">
                                <label className="block text-sm">Shipped date
                                  <Input type="date" value={draft.shipped_date ?? order.shipped_date ?? ''} onChange={(event) => edit('shipped_date', event.target.value)} />
                                </label>
                                <label className="block text-sm">Tracking label / number
                                  <Input value={draft.tracking_label ?? order.tracking_label ?? ''} maxLength={200} onChange={(event) => edit('tracking_label', event.target.value)} />
                                </label>
                                <label className="block text-sm">Shipping provider
                                  <Input value={draft.shipping_provider ?? order.shipping_provider ?? ''} maxLength={120} onChange={(event) => edit('shipping_provider', event.target.value)} />
                                </label>
                                <Button disabled={assistantActionBusy || !(draft.shipped_date ?? order.shipped_date)} onClick={async () => {
                                  setAssistantActionBusy(true);
                                  try {
                                    await updateAdminOrderTracker(id, { shipped_date: draft.shipped_date ?? order.shipped_date, tracking_label: draft.tracking_label ?? order.tracking_label ?? '', shipping_provider: draft.shipping_provider ?? order.shipping_provider ?? '' });
                                    toast.success(`Shipping details saved for ${id}`);
                                    await load();
                                    setAssistantAction(null);
                                  } catch (error) { toast.error(error?.response?.data?.detail || 'Could not save shipment'); }
                                  finally { setAssistantActionBusy(false); }
                                }}>Save shipping details</Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {!assistantAction.orderIds?.length && <Button type="button" onClick={() => { openDashboardDestination(assistantAction.destination); setAssistantAction(null); }}>Open {assistantAction.destination} details</Button>}
                    </div>
                  </div>
                )}
                {assistantResponse && (
                  <div className="mt-5 space-y-5">
                    {assistantResponse.actions.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-2">Actions to complete</h3>
                        <div className="space-y-2">
                          {assistantResponse.actions.map((action, index) => (
                            <button
                              type="button"
                              key={`${action.title}-${index}`}
                              onClick={() => setAssistantAction({ ...action, drafts: {} })}
                              className="w-full text-left border rounded-xl p-3 hover:bg-muted/50 transition-colors"
                            >
                              <p className="font-medium">{action.title}</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {action.detail}
                              </p>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {assistantResponse.actions.length === 0 &&
                      assistantResponse.highlights.length === 0 &&
                      assistantResponse.behavior.length === 0 && (
                        <p className="text-sm text-muted-foreground">
                          No matching operational insight was found. Try “things to do today”, “ordering highlights”, or “customer behavior”.
                        </p>
                      )}

                    {assistantResponse.highlights.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-2">Ordering highlights</h3>
                        <div className="grid sm:grid-cols-3 gap-3">
                          {assistantResponse.highlights.map((highlight) => (
                            <div key={highlight.label} className="border rounded-xl p-3">
                              <p className="text-xs text-muted-foreground">{highlight.label}</p>
                              <p className="text-lg font-semibold mt-1">{highlight.value}</p>
                              <p className="text-xs text-muted-foreground mt-1">{highlight.detail}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {assistantResponse.behavior.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-2">Customer behavior</h3>
                        <div className="border rounded-xl p-3 space-y-1">
                          {assistantResponse.behavior.map((line) => (
                            <p key={line} className="text-sm">{line}</p>
                          ))}
                          <Button
                            variant="link"
                            className="px-0 h-auto mt-2"
                            onClick={() => openDashboardDestination('Funnel')}
                          >
                            Open Customer Funnel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="text-2xl font-semibold">Order Tracker</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Purchase declared is recorded when the customer taps I've paid. Confirm payment manually here, then update batch, shipped date and customer received date.
                </p>
              </div>
              <Button onClick={exportOrdersCsv}>
                Export CSV
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { key: 'name', label: 'Customer name' },
                { key: 'phone', label: 'Phone number' },
                { key: 'address', label: 'Shipping address' },
              ].map(({ key, label }) => (
                <Input
                  key={key}
                  aria-label={`Filter by ${label.toLowerCase()}`}
                  placeholder={`Filter by ${label}...`}
                  value={orderFilters[key]}
                  onChange={(event) => setOrderFilters((previous) => ({ ...previous, [key]: event.target.value }))}
                />
              ))}
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground">Showing {filteredOrders.length} of {orders.length} orders</div>
              <Button type="button" onClick={() => openOrderEditor()}><Plus className="w-4 h-4 mr-2" />Create Order</Button>
            </div>
            {orderEditor && (
              <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-label={orderEditor.id ? 'Edit order' : 'Create order'}>
                <div className="bg-background rounded-xl shadow-xl p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto space-y-3">
                  <h3 className="font-semibold text-lg">{orderEditor.id ? `Edit Order ${orderEditor.id}` : 'Create Order'}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      ['customer_name', 'Customer Name'], ['customer_phone', 'Phone Number'],
                      ['line1', 'Address Line 1'], ['line2', 'Address Line 2'],
                      ['pincode', 'PIN Code'], ['city', 'City'], ['district', 'District'], ['state', 'State'],
                    ].map(([key, label]) => (
                      <label key={key} className="text-sm space-y-1"><span>{label}</span>
                        <Input value={orderEditor[key]} onChange={e => key === 'pincode' ? handleOrderPincode(e.target.value) : setOrderEditor(prev => ({ ...prev, [key]: e.target.value }))} />
                        {key === 'pincode' && orderPincodeLoading && <span className="text-xs text-muted-foreground">Looking up PIN code...</span>}
                      </label>
                    ))}
                    <label className="text-sm space-y-1"><span>Product</span>
                      <select className="w-full border rounded-md h-10 px-2 bg-background" disabled={!!orderEditor.id} value={orderEditor.product_slug} onChange={e => setOrderEditor(prev => ({ ...prev, product_slug: e.target.value, variant_id: '' }))}>
                        {products.map(p => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                      </select>
                    </label>
                    <label className="text-sm space-y-1"><span>Pack Size</span>
                      <select className="w-full border rounded-md h-10 px-2 bg-background" value={orderEditor.variant_id} onChange={e => setOrderEditor(prev => ({ ...prev, variant_id: e.target.value }))}>
                        <option value="">Select pack size</option>
                        {(products.find(p => p.slug === orderEditor.product_slug)?.variants || []).map(v => <option key={v.variant_id} value={v.variant_id}>{v.pack_size}</option>)}
                      </select>
                    </label>
                    <label className="text-sm space-y-1"><span>Quantity (packs)</span><Input type="number" min="1" value={orderEditor.quantity} onChange={e => setOrderEditor(prev => ({ ...prev, quantity: e.target.value }))} /></label>
                    <label className="text-sm space-y-1"><span>Purchase Declared Date & Time</span><Input type="datetime-local" value={orderEditor.purchase_declared_date} onChange={e => setOrderEditor(prev => ({ ...prev, purchase_declared_date: e.target.value }))} /></label>
                    {orderEditor.id && <label className="text-sm space-y-1"><span>Order Value (₹)</span><Input type="number" min="0" step="0.01" value={orderEditor.order_total} onChange={e => setOrderEditor(prev => ({ ...prev, order_total: e.target.value }))} /></label>}
                  </div>
                  {orderEditor.id && <label className="text-sm block space-y-1"><span>Reason for correction (required)</span><Input value={orderEditor.reason} onChange={e => setOrderEditor(prev => ({ ...prev, reason: e.target.value }))} placeholder="Explain why this order is being corrected" /></label>}
                  <p className="text-xs text-muted-foreground">{orderEditor.id ? 'Payment history remains unchanged; price adjustments are audited.' : 'New orders are created awaiting payment, never automatically marked paid.'}</p>
                  <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOrderEditor(null)} disabled={savingOrderEditor}>Cancel</Button><Button onClick={saveOrderEditor} disabled={savingOrderEditor}>{savingOrderEditor ? 'Saving...' : 'Save Order'}</Button></div>
                </div>
              </div>
            )}

            <Card>
              <CardContent className="p-0">
                <div className="max-h-[70vh] overflow-auto relative">
                <table className="w-full text-sm min-w-[1750px]">
                  <thead className="sticky top-0 z-40 border-b bg-muted shadow-sm">
                    <tr>
                      <th className="sticky left-0 z-50 w-[175px] min-w-[175px] bg-muted p-3 text-left font-semibold">Order ID</th>
                      <th className="sticky left-[175px] z-50 w-[130px] min-w-[130px] bg-muted p-3 text-left font-semibold">Actions</th>
                      <th className="sticky left-[305px] z-50 w-[150px] min-w-[150px] bg-muted p-3 text-left font-semibold">Customer Name</th>
                      <th className="sticky left-[455px] z-50 w-[140px] min-w-[140px] bg-muted p-3 text-left font-semibold">Phone Number/Email address</th>
                      <th className="sticky left-[595px] z-50 w-[250px] min-w-[250px] bg-muted p-3 text-left font-semibold shadow-[6px_0_8px_-6px_rgba(0,0,0,0.25)]">Shipping Address</th>
                      <th className="text-left p-3 font-semibold">Batch</th>
                      <th className="text-left p-3 font-semibold">Product</th>
                      <th className="text-left p-3 font-semibold">Farmer</th>
                      <th className="text-left p-3 font-semibold">Amount (INR)</th>
                      <th className="text-left p-3 font-semibold">Quantity</th>
                      <th className="text-left p-3 font-semibold">Purchase Declared</th>
                      <th className="text-left p-3 font-semibold">Payment Confirmed</th>
                      <th className="text-left p-3 font-semibold">Farmer Paid</th>
                      <th className="text-left p-3 font-semibold">Order Received</th>
                      <th className="text-left p-3 font-semibold">Farmer Communicated</th>
                      <th className="text-left p-3 font-semibold">Order Shipped</th>
                      <th className="text-left p-3 font-semibold">Customer Communicated</th>
                      <th className="text-left p-3 font-semibold">Shipping Weight (kg)</th>
                      <th className="text-left p-3 font-semibold">Shipping Cost (₹)</th>
                      <th className="text-left p-3 font-semibold">Customer Received</th>
                      <th className="text-left p-3 font-semibold">Customer Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan="20" className="p-4 text-muted-foreground">
                          No orders match the filters.
                        </td>
                      </tr>
                    ) : (
                      filteredOrders.map((order) => {
                        const timing = shippingTiming(order);
                        const timingClass =
                          timing.state === 'on-time'
                            ? 'bg-emerald-50'
                            : timing.state === 'late' || timing.state === 'overdue'
                              ? 'bg-orange-50'
                              : timing.state === 'warning'
                                ? 'bg-amber-50'
                                : '';
                        return (
                        <tr key={order.order_id} className={`border-b last:border-b-0 align-top ${timingClass}`}>
                          <td className={`sticky left-0 z-20 w-[175px] min-w-[175px] p-3 font-mono text-xs whitespace-nowrap ${timingClass || 'bg-background'}`} title={order.order_id}>{order.order_id || '—'}</td>
                          <td className={`sticky left-[175px] z-20 w-[130px] min-w-[130px] p-3 ${timingClass || 'bg-background'}`}>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800"
                              onClick={() => deleteOrder(order)}
                              title="Delete this order"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => openOrderEditor(order)}>Edit</Button>
                          </td>
                          <td className={`sticky left-[305px] z-20 w-[150px] min-w-[150px] p-3 ${timingClass || 'bg-background'}`}>{order.customer?.name || '—'}</td>
                          <td className={`sticky left-[455px] z-20 w-[140px] min-w-[140px] p-3 ${timingClass || 'bg-background'}`}><div>{order.customer?.phone || '—'}</div>{order.customer?.email && <div className="mt-1 text-xs break-all text-muted-foreground">{order.customer.email}</div>}</td>
                          <td className={`sticky left-[595px] z-20 w-[250px] min-w-[250px] p-3 shadow-[6px_0_8px_-6px_rgba(0,0,0,0.25)] ${timingClass || 'bg-background'}`}>
                            {order.delivery_address ? (
                              <div className="leading-5">
                                <div>{order.delivery_address.line1 || '—'}</div>
                                {order.delivery_address.line2 && <div>{order.delivery_address.line2}</div>}
                                <div>
                                  {[order.delivery_address.city, order.delivery_address.state]
                                    .filter(Boolean)
                                    .join(', ')}
                                  {order.delivery_address.pincode ? ` - ${order.delivery_address.pincode}` : ''}
                                </div>
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="p-3">
                            <Input
                              defaultValue={order.batch || ''}
                              placeholder="Batch"
                              className="min-w-[110px]"
                              onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (value !== (order.batch || '')) {
                                  updateOrderTracker(
                                    order.order_id,
                                    { batch: value },
                                    'Batch updated'
                                  );
                                }
                              }}
                            />
                          </td>
                          <td className="p-3">
                            <div>{order.item?.name || '—'}</div>
                            {order.item?.pack_size && (
                              <div className="text-xs text-muted-foreground">{order.item.pack_size}</div>
                            )}
                          </td>
                          <td className="p-3">{order.farmer?.name || '—'}</td>
                          <td className="p-3">₹{order.pricing?.total ?? 0}</td>
                          <td className="p-3">{order.item?.quantity ?? '—'}</td>
                          <td className="p-3 whitespace-nowrap">
                            {order.customer_declared_paid_at
                              ? new Date(order.customer_declared_paid_at).toLocaleString()
                              : '—'}
                          </td>
                          <td className="p-3 min-w-[190px]">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={order.payment_status === 'PAYMENT_CONFIRMED'}
                                disabled={
                                  order.payment_status !== 'PAYMENT_SUBMITTED' &&
                                  order.payment_status !== 'AWAITING_PAYMENT' &&
                                  order.payment_status !== 'EXPIRED' &&
                                  order.payment_status !== 'PAYMENT_CONFIRMED'
                                }
                                onClick={() => {
                                  if (order.payment_status === 'PAYMENT_SUBMITTED' ||
                                  order.payment_status === 'AWAITING_PAYMENT' || order.payment_status === 'EXPIRED') {
                                    confirmPayment(order.order_id);
                                  } else if (order.payment_status === 'PAYMENT_CONFIRMED') {
                                    unconfirmPayment(order.order_id);
                                  }
                                }}
                                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                                  order.payment_status === 'PAYMENT_CONFIRMED'
                                    ? 'bg-emerald-700'
                                    : 'bg-gray-300'
                                } ${
                                  order.payment_status === 'PAYMENT_SUBMITTED' ||
                                  order.payment_status === 'AWAITING_PAYMENT' ||
                                  order.payment_status === 'EXPIRED' ||
                                  order.payment_status === 'PAYMENT_CONFIRMED'
                                    ? 'cursor-pointer'
                                    : 'cursor-not-allowed opacity-60'
                                }`}
                                title={
                                  order.payment_status === 'PAYMENT_CONFIRMED'
                                    ? 'Slide to No to remove payment confirmation'
                                    : order.payment_status === 'PAYMENT_SUBMITTED' || order.payment_status === 'AWAITING_PAYMENT' || order.payment_status === 'EXPIRED'
                                      ? 'Slide to Yes to confirm payment'
                                      : 'Payment cannot be confirmed for this status'
                                }
                              >
                                <span
                                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                    order.payment_status === 'PAYMENT_CONFIRMED'
                                      ? 'translate-x-5'
                                      : 'translate-x-0.5'
                                  }`}
                                />
                              </button>
                              <span className="text-xs font-medium">
                                {order.payment_status === 'PAYMENT_CONFIRMED' ? 'Yes' : 'No'}
                              </span>
                            </div>
                            {order.payment_confirmed_at && (
                              <div className="mt-1 text-[11px] leading-4 text-muted-foreground whitespace-normal">
                                {order.payment_confirmed_by || 'Admin'} ·{' '}
                                {new Date(order.payment_confirmed_at).toLocaleString()}
                              </div>
                            )}
                          </td>
                          <td className="p-3 min-w-[190px]">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={Boolean(order.farmer_paid)}
                                onClick={() =>
                                  updateOrderTracker(
                                    order.order_id,
                                    { farmer_paid: !Boolean(order.farmer_paid) },
                                    !order.farmer_paid
                                      ? 'Farmer marked paid'
                                      : 'Farmer marked unpaid'
                                  )
                                }
                                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                                  order.farmer_paid
                                    ? 'bg-emerald-700'
                                    : 'bg-gray-300'
                                } cursor-pointer`}
                                title={order.farmer_paid ? 'Slide to No' : 'Slide to Yes'}
                              >
                                <span
                                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                    order.farmer_paid
                                      ? 'translate-x-5'
                                      : 'translate-x-0.5'
                                  }`}
                                />
                              </button>
                              <span className="text-xs font-medium">
                                {order.farmer_paid ? 'Yes' : 'No'}
                              </span>
                            </div>
                            <div className="mt-1 text-[11px] leading-4 text-muted-foreground whitespace-nowrap">
                              To disburse: ₹{Number(order.pricing?.farmer_price ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </div>
                            {(order.farmer_paid_updated_at || order.farmer_paid_at) && (
                              <div className="mt-1 text-[11px] leading-4 text-muted-foreground whitespace-normal">
                                {order.farmer_paid_updated_by || order.farmer_paid_by || 'Admin'} ·{' '}
                                {new Date(
                                  order.farmer_paid_updated_at || order.farmer_paid_at
                                ).toLocaleString()}
                              </div>
                            )}
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            {order.created_at
                              ? new Date(order.created_at).toLocaleString()
                              : '—'}
                          </td>
                          <td className="p-3 min-w-[180px]">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={Boolean(order.farmer_communicated)}
                                aria-label={`Farmer communicated for order ${order.order_id}`}
                                onClick={() => updateOrderTracker(
                                  order.order_id,
                                  { farmer_communicated: !Boolean(order.farmer_communicated) },
                                  !order.farmer_communicated ? 'Farmer communication recorded' : 'Farmer communication reset'
                                )}
                                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${order.farmer_communicated ? 'bg-emerald-700' : 'bg-gray-300'}`}
                              >
                                <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${order.farmer_communicated ? 'translate-x-5' : 'translate-x-0.5'}`} />
                              </button>
                              <span className="text-xs font-medium">{order.farmer_communicated ? 'Yes' : 'No'}</span>
                            </div>
                            {order.farmer_communicated_at && (
                              <div className="mt-1 text-[11px] text-muted-foreground">
                                {order.farmer_communicated_by || 'Admin'} · {new Date(order.farmer_communicated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                              </div>
                            )}
                          </td>
                          <td className="p-3">
                            <Input
                              type="date"
                              value={
                                order.shipped_date ||
                                (order.shipped_at ? String(order.shipped_at).slice(0, 10) : '')
                              }
                              onChange={(e) =>
                                updateOrderTracker(
                                  order.order_id,
                                  { shipped_date: e.target.value },
                                  'Shipped date updated'
                                )
                              }
                              className="min-w-[145px]"
                            />
                            <div className="mt-2 space-y-2 min-w-[180px]">
                              <Input
                                key={`${order.order_id}-tracking-label-${order.tracking_label ?? ''}`}
                                type="text"
                                maxLength={200}
                                defaultValue={order.tracking_label || ''}
                                placeholder="Tracking label"
                                aria-label={`Tracking label for order ${order.order_id}`}
                                onBlur={(event) => {
                                  const value = event.target.value.trim();
                                  if (value !== (order.tracking_label || '')) {
                                    updateOrderTracker(order.order_id, { tracking_label: value }, 'Tracking label updated');
                                  }
                                }}
                              />
                              <Input
                                key={`${order.order_id}-shipping-provider-${order.shipping_provider ?? ''}`}
                                type="text"
                                maxLength={120}
                                defaultValue={order.shipping_provider || ''}
                                placeholder="Provider name"
                                aria-label={`Shipping provider for order ${order.order_id}`}
                                onBlur={(event) => {
                                  const value = event.target.value.trim();
                                  if (value !== (order.shipping_provider || '')) {
                                    updateOrderTracker(order.order_id, { shipping_provider: value }, 'Shipping provider updated');
                                  }
                                }}
                              />
                            </div>
                            {timing.state === 'late' && timing.delayHours > 0 && (
                              <div className="mt-1 text-xs font-medium text-orange-700">
                                Shipped {timing.delayHours < 24
                                  ? `${Math.ceil(timing.delayHours)} hr late vs 72-hr ETA`
                                  : `${(timing.delayHours / 24).toFixed(1)} days late vs 72-hr ETA`}
                              </div>
                            )}
                            {timing.state === 'overdue' && timing.delayHours > 0 && (
                              <div className="mt-1 text-xs font-medium text-orange-700">
                                Overdue by {timing.delayHours < 24
                                  ? `${Math.ceil(timing.delayHours)} hr`
                                  : `${(timing.delayHours / 24).toFixed(1)} days`} vs 72-hr ETA
                              </div>
                            )}
                          </td>
                          <td className="p-3 min-w-[185px]">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={Boolean(order.customer_communicated)}
                                aria-label={`Customer communicated for order ${order.order_id}`}
                                onClick={() => updateOrderTracker(
                                  order.order_id,
                                  { customer_communicated: !Boolean(order.customer_communicated) },
                                  !order.customer_communicated ? 'Customer communication recorded' : 'Customer communication reset'
                                )}
                                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${order.customer_communicated ? 'bg-emerald-700' : 'bg-gray-300'}`}
                              >
                                <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${order.customer_communicated ? 'translate-x-5' : 'translate-x-0.5'}`} />
                              </button>
                              <span className="text-xs font-medium">{order.customer_communicated ? 'Yes' : 'No'}</span>
                            </div>
                            {order.customer_communicated && order.customer_communicated_at && (
                              <div className="mt-1 text-[11px] text-muted-foreground">
                                {order.customer_communicated_by || 'Admin'} · {new Date(order.customer_communicated_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                              </div>
                            )}
                          </td>
                          <td className="p-3 min-w-[140px]">
                            <div className="relative">
                            <Input
                              key={`${order.order_id}-shipping-weight-${order.shipping_weight_kg ?? 'blank'}`}
                              type="number"
                              min="0"
                              step="0.001"
                              inputMode="decimal"
                              defaultValue={order.shipping_weight_kg ?? ''}
                              placeholder="0.000"
                              aria-label={`Shipping weight in kilograms for order ${order.order_id}`}
                              onBlur={(e) => {
                                const raw = e.target.value.trim();
                                const value = raw === '' ? null : Number(raw);
                                if (raw !== '' && (!Number.isFinite(value) || value < 0)) {
                                  toast.error('Enter a valid shipping weight of 0 kg or more');
                                  e.target.value = order.shipping_weight_kg ?? '';
                                  return;
                                }
                                if (value !== (order.shipping_weight_kg ?? null)) {
                                  updateOrderTracker(
                                    order.order_id,
                                    { shipping_weight_kg: value },
                                    'Shipping weight updated'
                                  );
                                }
                              }}
                              className="min-w-[125px] pr-11"
                            />
                          
                            <span aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">kg</span>
                            </div>
                          </td>
                          <td className="p-3 min-w-[140px]">
                            <div className="relative">
                              <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">₹</span>
                            <Input
                              key={`${order.order_id}-shipping-${order.shipping_cost_inr ?? 'blank'}`}
                              type="number"
                              min="0"
                              step="0.01"
                              inputMode="decimal"
                              defaultValue={order.shipping_cost_inr ?? ''}
                              placeholder="0.00"
                              aria-label={`Shipping cost in rupees for order ${order.order_id}`}
                              onBlur={(e) => {
                                const raw = e.target.value.trim();
                                const value = raw === '' ? null : Number(raw);
                                if (raw !== '' && (!Number.isFinite(value) || value < 0)) {
                                  toast.error('Enter a valid shipping cost of ₹0 or more');
                                  e.target.value = order.shipping_cost_inr ?? '';
                                  return;
                                }
                                if (value !== (order.shipping_cost_inr ?? null)) {
                                  updateOrderTracker(
                                    order.order_id,
                                    { shipping_cost_inr: value },
                                    'Shipping cost updated'
                                  );
                                }
                              }}
                              className="min-w-[125px] pl-7"
                            />
                            </div>
                          </td>
                          <td className="p-3">
                            <Input
                              type="date"
                              value={
                                order.customer_received_date ||
                                (order.delivered_at ? String(order.delivered_at).slice(0, 10) : '')
                              }
                              onChange={(e) =>
                                updateOrderTracker(
                                  order.order_id,
                                  { customer_received_date: e.target.value },
                                  'Customer received date updated'
                                )
                              }
                              className="min-w-[145px]"
                            />
                          </td>
                          <td className="p-3 min-w-[220px]">
                            <Input
                              type="text"
                              defaultValue={order.customer_remarks || ''}
                              placeholder="Customer remarks"
                              maxLength={500}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value !== (order.customer_remarks || '')) {
                                  updateOrderTracker(
                                    order.order_id,
                                    { customer_remarks: value || null },
                                    'Customer remarks updated'
                                  );
                                }
                              }}
                            />
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}


        {tab === 'Funnel' && (
          <div className="space-y-5">
            <div>
              <h2 className="text-2xl font-semibold">Web traffic</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Anonymous activity recorded from 0middle.com. Each browser gets a visitor ID so you
                can see visits and the steps taken before checkout or purchase.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Stat
                label="Visitors"
                value={customerFunnel?.visitors ?? '—'}
                note="Unique browser IDs"
              />
              <Stat
                label="Checkout started"
                value={customerFunnel?.checkout_started ?? '—'}
                note="Entered delivery details"
              />
              <Stat
                label="Payment screen"
                value={customerFunnel?.payment_viewed ?? '—'}
                note="Reached UPI payment"
              />
              <Stat
                label="Purchase declared"
                value={customerFunnel?.purchased ?? '—'}
                note="Tapped I’ve paid"
              />
              <Stat
                label="Abandoned"
                value={customerFunnel?.abandoned_count ?? '—'}
                note="No purchase after 30 min"
              />
            </div>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm min-w-[1280px]">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      <th className="text-left p-3 font-semibold">Date & time</th>
                      <th className="text-left p-3 font-semibold">IP address</th>
                      <th className="text-left p-3 font-semibold">Visitor</th>
                      <th className="text-left p-3 font-semibold">Events</th>
                      <th className="text-left p-3 font-semibold">Geography</th>
                      <th className="text-left p-3 font-semibold">Timezone</th>
                      <th className="text-left p-3 font-semibold">Language</th>
                      <th className="text-left p-3 font-semibold">Screen</th>
                      <th className="text-left p-3 font-semibold">Referrer</th>
                      <th className="text-left p-3 font-semibold">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedWebTraffic.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="p-4 text-muted-foreground">
                          No web traffic events recorded yet.
                        </td>
                      </tr>
                    ) : (
                      groupedWebTraffic.map((group) => (
                        <tr
                          key={group.key}
                          className={`border-b last:border-b-0 align-top ${
                            group.converted ? 'bg-emerald-50/70' : ''
                          }`}
                        >
                          <td className="p-3 whitespace-nowrap">
                            <div>
                              {group.first_seen
                                ? new Date(group.first_seen).toLocaleString()
                                : '—'}
                            </div>
                            {group.last_seen && group.last_seen !== group.first_seen && (
                              <div className="text-xs text-muted-foreground mt-1">
                                to {new Date(group.last_seen).toLocaleString()}
                              </div>
                            )}
                          </td>
                          <td className="p-3 font-mono text-xs">
                            {group.ip_address || '—'}
                          </td>
                          <td className="p-3 font-mono text-xs">
                            {group.visitors.length
                              ? group.visitors.map((visitor) => (
                                  <div key={visitor}>
                                    {visitor.length > 12 ? `${visitor.slice(0, 12)}…` : visitor}
                                  </div>
                                ))
                              : '—'}
                          </td>
                          <td className="p-3 min-w-[330px]">
                            <div className="space-y-2">
                              {group.events.map((entry, index) => (
                                <div
                                  key={`${entry.created_at}-${entry.event}-${index}`}
                                  className="flex flex-wrap items-center gap-2"
                                >
                                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                                    {entry.created_at
                                      ? new Date(entry.created_at).toLocaleTimeString()
                                      : ''}
                                  </span>
                                  <Badge
                                    variant="outline"
                                    className={
                                      entry.event === 'PURCHASE_DECLARED'
                                        ? 'border-emerald-600 bg-emerald-100 text-emerald-900'
                                        : ''
                                    }
                                  >
                                    {entry.event || '—'}
                                  </Badge>
                                  <span className="text-xs break-all">
                                    {entry.path || '—'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            {[group.city, group.region, group.country]
                              .filter(Boolean)
                              .join(', ') || '—'}
                          </td>
                          <td className="p-3">{group.timezone || '—'}</td>
                          <td className="p-3">{group.language || '—'}</td>
                          <td className="p-3">{group.screen || '—'}</td>
                          <td className="p-3 max-w-[240px] truncate" title={group.referrer || ''}>
                            {group.referrer || 'Direct / unknown'}
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            {group.converted ? (
                              <Badge className="bg-emerald-700 text-white">
                                Sale
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <p className="text-xs text-muted-foreground">
              Privacy note: web traffic records an anonymous browser ID, IP address, basic browser
              metadata and approximate city / region / country derived from the IP address.
              IP-based geography is approximate and may be affected by mobile networks, VPNs or proxies.
            </p>
          </div>
        )}

        {tab === 'Testing' && <TestRequirements />}

        {tab === 'Audit' && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold">Audit Trail</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  A running history of admin changes.
                </p>
              </div>
              <Button
                type="button"
                variant="destructive"
                onClick={clearAuditTrail}
                disabled={auditTrail.length === 0}
              >
                Clear audit trail
              </Button>
            </div>

            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <div className="px-3 py-3 border-b bg-background text-sm">
                  <span className="font-semibold mr-3">Shipping status · promised ETA: 24–72 hrs from order placement</span>
                  <span className="inline-flex items-center gap-1.5 mr-4"><span className="w-3 h-3 rounded-sm bg-emerald-50 border" />Green — shipped within 72 hrs</span>
                  <span className="inline-flex items-center gap-1.5 mr-4"><span className="w-3 h-3 rounded-sm bg-amber-50 border" />Yellow — unshipped, 48–72 hrs</span>
                  <span className="inline-flex items-center gap-1.5 mr-4"><span className="w-3 h-3 rounded-sm bg-orange-50 border" />Orange — shipped late or still unshipped after 72 hrs</span>
                  <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-white border" />White — under 48 hrs, not yet shipped</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="border-b bg-muted/40">
                    <tr>
                      <th className="text-left p-3 font-semibold">What changed?</th>
                      <th className="text-left p-3 font-semibold">Date and time</th>
                      <th className="text-left p-3 font-semibold">Change done by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditTrail.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="p-4 text-muted-foreground">
                          No audit entries yet.
                        </td>
                      </tr>
                    ) : (
                      auditTrail.map((entry, index) => (
                        <tr
                          key={`${entry.changed_at}-${index}`}
                          className="border-b last:border-b-0"
                        >
                          <td className="p-3">{entry.change}</td>
                          <td className="p-3 whitespace-nowrap">
                            {new Date(entry.changed_at).toLocaleString()}
                          </td>
                          <td className="p-3">{entry.changed_by}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}


      </main>
      {spellcheckDialog && (
        <div className="fixed inset-0 z-[70] bg-black/45 flex items-center justify-center p-4">
          <div className="bg-card border rounded-2xl shadow-2xl w-full max-w-lg p-6" role="dialog" aria-modal="true" aria-label="Spelling suggestions">
            <h2 className="text-xl font-semibold">Possible spelling issues</h2>
            <div className="mt-4 space-y-2 text-sm">
              {spellcheckDialog.issues.slice(0, 8).map((issue) => (
                <div key={issue.word} className="flex justify-between gap-4 border-b pb-2"><span>{issue.word}</span><span className="font-medium">{issue.suggestion ? "→ " + issue.suggestion : "Review"}</span></div>
              ))}
            </div>
            <p className="text-sm text-muted-foreground mt-4">Accept Changes applies the suggested replacements. Keep as Written saves your original text. Review returns to editing.</p>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button variant="outline" onClick={() => setSpellcheckDialog(null)}>Review</Button>
              <Button variant="outline" onClick={keepFarmerSpelling}>Keep as Written</Button>
              <Button onClick={acceptFarmerSpelling} disabled={!spellcheckDialog.issues.some((issue) => issue.suggestion)}>Accept Changes</Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

const FarmerDetail = ({ label, value }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-1 whitespace-pre-wrap">{value || '—'}</p>
  </div>
);

const Stat = ({ label, value, note, onClick }) => {
  const content = (
    <CardContent className="p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold mt-1">{value}</p>
      {note && (
        <p className="text-xs text-muted-foreground mt-2">{note}</p>
      )}
      {onClick && (
        <p className="text-xs text-primary mt-2 font-medium">Open →</p>
      )}
    </CardContent>
  );

  if (!onClick) {
    return <Card>{content}</Card>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left w-full"
    >
      <Card className="h-full hover:bg-muted/40 hover:shadow-sm transition-all cursor-pointer">
        {content}
      </Card>
    </button>
  );
};

const QuickAction = ({ icon: Icon, title, text, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-left border rounded-xl p-4 hover:bg-muted/50 transition-colors"
  >
    <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
      <Icon className="w-4 h-4" />
    </div>
    <div className="flex items-center gap-1 mt-3">
      <p className="font-semibold">{title}</p>
      <Plus className="w-4 h-4 text-muted-foreground" />
    </div>
    <p className="text-sm text-muted-foreground mt-1">{text}</p>
  </button>
);

const Field = ({ label, value, onChange, type = 'text' }) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <Input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1"
    />
  </div>
);

const SelectUnit = ({ label, value, onChange }) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <select
      className="w-full border rounded-md h-10 px-3 bg-background mt-1"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="ml">ml</option>
      <option value="L">L</option>
      <option value="mg">mg</option>
                                  <option value="g">g</option>
      <option value="kg">kg</option>
      <option value="count">count</option>
    </select>
  </div>
);

const SelectInventoryUnit = SelectUnit;

export default AdminDashboard;

